import React, { useState, useEffect, useContext } from 'react';
import {
  Plus, Edit2, Trash2, Save, X, Search, Filter,
  CheckCircle2, AlertCircle, Loader2, Eye, Settings,
  Upload, Download, FileText, Brain, Heart, Activity,
  Zap, Users, Smile, Package, MoreHorizontal, ChevronRight,
  Hash, Check, RotateCcw
} from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import RichTextEditor from './RichTextEditor';
import NumericInput from './ScreenBuilder/NumericInput';
import permissionsService from '../../services/permissionsService';
import ConfirmDialog from '../common/ConfirmDialog';
import { RESPONSE_OPTION_PRESETS, PRESET_LABELS } from './ScreenBuilder/constants';
import { htmlToPlainText } from './ScreenBuilder/utils';
import api from '../../services/api';
import * as AssessmentBuilderAPI from '../../services/assessmentBuilderApi';
import ConfigureScreenLayoutModal from './ConfigureScreenLayoutModal';
import FreeTextValidationSettings, {
  EMPTY_FREE_TEXT_VALIDATION_DRAFT,
  freeTextValidationToDraft,
  resolveFreeTextValidationDraft,
} from './FreeTextValidationSettings';
import { describeFreeTextValidation, getFreeTextValidation } from '../../utils/freeTextValidation';

// Stored types the Response Type dropdown spells differently (FECOA-2041).
// Without this a yes_no or nrs item matches no option and the select shows
// "Ordinal (Likert-type)".
const RESPONSE_TYPE_ALIASES = {
  yes_no: 'boolean',
  nrs: 'numeric',
  likert: 'ordinal',
};

// ─── Item bank cache (persists across modal open/close) ─────────────────────
const itemBankCache = {
  data: {},     // keyed by `${library}:${assessmentId}`
  timestamps: {},
  TTL: 60_000,  // 60 seconds — fresh enough, avoids redundant fetches on re-open

  get(key) {
    const ts = this.timestamps[key];
    if (ts && Date.now() - ts < this.TTL && this.data[key]) {
      return this.data[key];
    }
    return null;
  },
  set(key, value) {
    this.data[key] = value;
    this.timestamps[key] = Date.now();
  },
  invalidate(key) {
    if (key) {
      delete this.data[key];
      delete this.timestamps[key];
    } else {
      // Invalidate all
      this.data = {};
      this.timestamps = {};
    }
  },
};

const UnifiedItemBank = ({
  isOpen,
  onClose,
  onAddItem,
  onItemDeleted, // Callback when item is deleted - for refreshing parent state
  onScreensBuilt, // Callback to trigger ScreenBuilder re-fetch after auto-build
  libraryType: initialLibraryType = 'assessment',
  currentAssessment = null, // For "Add to Assessment" context
  assessmentItems = [], // Items already in the assessment
  initialEditItem = null, // Item to auto-open in edit form when modal opens
  onInitialEditHandled = null, // Callback to clear initialEditItem after handling
  onItemUpdated = null, // Callback when an item is edited - for refreshing sidebar items
  deviceMode = 'ipad-10' // Current device mode for template sorting
}) => {
  const { user, userPermissions } = useContext(AuthContext);
  const fileInputRef = React.useRef(null);
  const itemCodeRef = React.useRef(null);
  const questionTextRef = React.useRef(null);
  const responseOptionsRef = React.useRef(null);
  const freeTextValidationRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Configure Screen Layout modal state (shown after import → Add to Assessment)
  const [showConfigureLayout, setShowConfigureLayout] = useState(false);
  const [pendingSelectedItems, setPendingSelectedItems] = useState([]);
  
  // Library selector state
  const [activeLibrary, setActiveLibrary] = useState(initialLibraryType);
  
  // View modes
  const [viewMode, setViewMode] = useState('browse'); // 'browse' or 'manage'
  const [activeTab, setActiveTab] = useState('items'); // 'items' or 'categories'
  
  // Item type selection: 'standard' or 'irt'
  const [itemTypeFilter, setItemTypeFilter] = useState('standard');
  
  // Expanded item sets (questionnaires)
  const [expandedSets, setExpandedSets] = useState(new Set());
  
  // Selected item set for filtering center content
  const [selectedSet, setSelectedSet] = useState(null);
  
  // Data
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Browse/filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null); // For preview panel
  
  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]); // Array of item IDs
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Forms
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    id: '', code: '', text: '', category: '', domain: '', 
    responseType: 'ordinal', responseOptions: [], tags: '', 
    source: '', validated: false,
    library: 'assessment', // Where to save: 'assessment', 'study', 'sponsor', or 'vpg'
    // IRT fields for adaptive testing
    irtParameters: {},
    irtCategory: null,  // C=Calibrated, U=Uncalibrated, E=Excluded, S=Screening
    slope: null,
    thresholds: null,
    // VAS-specific fields
    vasLeftAnchor: '',
    vasRightAnchor: '',
    // Numeric-specific fields
    numericMin: 0,
    numericMax: 10,
    numericStep: 1,
    numericLeftAnchor: '',
    numericRightAnchor: '',
    // Text-specific fields
    textMaxLength: 500,
    textMultiline: true,
    textValidation: { ...EMPTY_FREE_TEXT_VALIDATION_DRAFT },
    // Boolean-specific fields
    booleanYesLabel: 'Yes',
    booleanNoLabel: 'No'
  });
  
  // Category creation state
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // Form-level error (shows inside item creation modal)
  const [formError, setFormError] = useState('');
  
  // UI state
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ 
    show: false, title: '', message: '', onConfirm: null 
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importTargetLibrary, setImportTargetLibrary] = useState('sponsor');  // 'study', 'sponsor', or 'vpg' - default to sponsor since study requires assessment
  const [importErrors, setImportErrors] = useState([]); // Errors to display inside import modal
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, isImporting: false }); // Import progress tracking
  const [importSummary, setImportSummary] = useState({ show: false, items: [], errors: [], selectedIds: new Set() }); // Import summary/review screen
  const [pendingFile, setPendingFile] = useState(null); // Hold file until library is selected
  const [showAddToAssessmentModal, setShowAddToAssessmentModal] = useState(false);
  const [previewItem, setPreviewItem] = useState(null); // For item preview modal
  
  // VPG Library state
  const [showVPGLibrary, setShowVPGLibrary] = useState(false);
  const [vpgTab, setVpgTab] = useState('questionnaires'); // 'questionnaires' or 'search'
  const [questionnaires, setQuestionnaires] = useState([]);
  const [vpgQuestions, setVpgQuestions] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [selectedVPGQuestions, setSelectedVPGQuestions] = useState([]);
  const [vpgLoading, setVpgLoading] = useState(false);
  const [vpgError, setVpgError] = useState(''); // Error to display inside VPG modal
  const [vpgSearchQuery, setVpgSearchQuery] = useState('');
  const [vpgDomainFilter, setVpgDomainFilter] = useState('');
  
  // VPG curation permissions (FECOA-902 + VPG access audit). Server enforces
  // these too — the flags only control which affordances render.
  const [canDeleteVPGItems, setCanDeleteVPGItems] = useState(false);
  const [canEditVPGItems, setCanEditVPGItems] = useState(false);
  const [canPublishVPGItems, setCanPublishVPGItems] = useState(false);

  const iconMap = { FileText, Brain, Heart, Activity, Zap, Eye, Users, Smile };
  const responseTypes = [
    { value: 'ordinal', label: 'Ordinal (Likert-type)' },
    { value: 'numeric', label: 'Numeric Rating Scale' },
    { value: 'vas', label: 'Visual Analog Scale' },
    { value: 'nominal', label: 'Nominal (Categories)' },
    { value: 'text', label: 'Free Text' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'instruction', label: 'Instruction' },
    { value: 'body_map', label: 'Body Map' }
  ];

  // Check VPG curation permissions on mount (FECOA-902)
  useEffect(() => {
    const checkVPGPermissions = async () => {
      const [canDelete, canEdit, canPublish] = await Promise.all([
        permissionsService.hasPermission(permissionsService.PERMISSIONS.DELETE_VPG_ITEM),
        permissionsService.hasPermission(permissionsService.PERMISSIONS.CREATE_VPG_ITEM),
        permissionsService.hasPermission(permissionsService.PERMISSIONS.PUBLISH_VPG_ITEM),
      ]);
      setCanDeleteVPGItems(canDelete || user?.is_superuser);
      setCanEditVPGItems(canEdit || user?.is_superuser);
      setCanPublishVPGItems(canPublish || user?.is_superuser);
    };
    checkVPGPermissions();
  }, [user]);

  // Clear notifications when modal opens
  useEffect(() => {
    if (isOpen) {
      setSaveStatus({ type: '', message: '' });
    }
  }, [isOpen]);

  // Edits launched from inside the builder always target the assessment-level
  // copy (the sidebar item's own id) — never the source library row. Library
  // items (VPG/sponsor/study) are only editable from their own library tier in
  // Manage mode, by users holding the matching permission.
  useEffect(() => {
    if (isOpen && initialEditItem) {
      setViewMode('manage');
      const normalized = {
        ...initialEditItem,
        responseType: initialEditItem.responseType || initialEditItem.response_type || initialEditItem.type || 'ordinal',
        responseOptions: initialEditItem.responseOptions || initialEditItem.response_options || initialEditItem.options || [],
        tags: initialEditItem.tags || [],
        _sidebarItemId: initialEditItem.id,
      };
      handleEditItem(normalized);
      if (onInitialEditHandled) onInitialEditHandled();
    }
  }, [isOpen, initialEditItem]);

  // VPG Manage mode is only available to users holding a VPG curation
  // permission; snap back to Browse when switching to VPG without one.
  const canManageVPGLibrary = canEditVPGItems || canDeleteVPGItems || canPublishVPGItems;
  useEffect(() => {
    if (activeLibrary === 'vpg' && viewMode === 'manage' && !canManageVPGLibrary) {
      setViewMode('browse');
    }
  }, [activeLibrary, viewMode, canManageVPGLibrary]);

  // Load data on open or when library changes
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, activeLibrary, currentAssessment?.assessment_id, assessmentItems]);

  // Filter items when search/category changes
  useEffect(() => {
    filterItems();
  }, [searchQuery, selectedCategory, items]);

  // If IRT gets disabled in assessment settings while the user is on the IRT
  // tab, fall back to Standard so they're not stuck on a hidden filter.
  useEffect(() => {
    if (!currentAssessment?.builder_metadata?.enable_irt_parameters && itemTypeFilter === 'irt') {
      setItemTypeFilter('standard');
    }
  }, [currentAssessment?.builder_metadata?.enable_irt_parameters, itemTypeFilter]);

  const cacheKey = `${activeLibrary}:${currentAssessment?.assessment_id || ''}`;

  // Invalidate cache and reload — call after any create/update/delete/import
  const invalidateAndReload = ({ silent = false } = {}) => {
    itemBankCache.invalidate(cacheKey);
    loadData({ silent });
  };

  const loadData = async ({ silent = false } = {}) => {
    // Check cache first
    const cached = itemBankCache.get(cacheKey);
    if (cached) {
      console.log(`[ITEM BANK] Cache hit for ${cacheKey} (${cached.items.length} items)`);
      setItems(cached.items);
      setCategories(cached.categories);
      setLoading(false);
      return;
    }

    if (!silent) setLoading(true);
    try {
      // Build URL with query params - include assessment_id for assessment + study libraries
      const params = new URLSearchParams({ library: activeLibrary });
      if ((activeLibrary === 'assessment' || activeLibrary === 'study') && currentAssessment?.assessment_id) {
        params.append('assessment_id', currentAssessment.assessment_id);
      }

      // Use api instance for automatic token refresh on 401
      const response = await api.get(`/api/assessment-builder/item-bank/?${params}`);
      const data = response.data;
      
      if (data) {
        
        // Map API response to frontend format, including IRT and questionnaire data
        const mappedItems = data.items.map(item => ({
          id: item.id,
          code: item.code,
          text: item.text,
          category: item.category || '',
          domain: item.domain || '',
          questionnaireId: item.questionnaire_id || item.domain || item.category || 'uncategorized',
          responseType: item.response_type || item.type,
          responseOptions: item.options || [],
          source: item.source || '',
          validated: item.validated || false,
          tags: item.tags || [],
          // IRT-related fields for adaptive testing
          irtParameters: item.irt_parameters || {},
          irtCategory: item.adaptive_category || null,  // C=Calibrated, U=Uncalibrated, E=Excluded, S=Screening
          slope: item.slope || null,
          thresholds: item.thresholds || null,
          isPublished: item.is_published !== false,  // Default to true for backwards compatibility
          libraryLevel: item.library_level || 'sponsor',  // Track library level for filtering
          isModified: !!item.is_modified,  // Drives the "Modified" pill on edited library copies
          baselineSnapshot: item.baseline_snapshot || null  // Drives the "Revert to baseline" action
        }));
        
        setItems(mappedItems);

        // Extract unique categories from items
        const uniqueCategories = [...new Set(mappedItems.map(item => item.category).filter(Boolean))];
        const mappedCategories = uniqueCategories.map(cat => ({ id: cat, name: cat }));
        setCategories(mappedCategories);

        // Cache the results
        itemBankCache.set(cacheKey, { items: mappedItems, categories: mappedCategories });
        console.log(`[ITEM BANK] Loaded ${mappedItems.length} items from API, cached as ${cacheKey}`);
      }
    } catch (error) {
      // Handle errors - api interceptor will auto-refresh token on 401
      // If we get here, either refresh failed (redirect to login) or other error
      console.error('[ITEM BANK] Error loading items:', error);
      setItems([]);
      setCategories([]);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        setSaveStatus({ type: 'error', message: 'Session expired or unauthorized. Please refresh and log in again.' });
      } else {
        setSaveStatus({ type: 'error', message: 'Unable to load item bank. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const loadFromLocalStorage = () => {
    // Fallback: Load from localStorage
    const catKey = `itembank_categories_${activeLibrary}`;
    const storedCats = localStorage.getItem(catKey);
    if (storedCats) {
      setCategories(JSON.parse(storedCats));
    }

    const itemKey = `itembank_items_${activeLibrary}`;
    const storedItems = localStorage.getItem(itemKey);
    setItems(storedItems ? JSON.parse(storedItems) : []);
  };

  const filterItems = () => {
    let filtered = [...items];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => {
        const tags = Array.isArray(item.tags) ? item.tags : (item.tags ? item.tags.split(',').map(t => t.trim()) : []);
        return item.text.toLowerCase().includes(query) ||
          item.code.toLowerCase().includes(query) ||
          tags.some(tag => tag.toLowerCase().includes(query)) ||
          (item.domain && item.domain.toLowerCase().includes(query));
      });
    }

    setFilteredItems(filtered);
  };

  // ============ Multi-Select Handlers ============
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAll = () => {
    setSelectedItems(filteredItems.map(item => item.id));
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectionMode(false);
  };

  const handleBulkAddToAssessment = () => {
    setShowAddToAssessmentModal(true);
  };

  const handleBulkDelete = () => {
    // Filter out any undefined/null IDs to prevent API errors
    const validItemIds = selectedItems.filter(id => id != null);
    
    if (validItemIds.length === 0) {
      setSaveStatus({ type: 'warning', message: 'No valid items to delete' });
      clearSelection();
      return;
    }
    
    setConfirmModal({
      show: true,
      title: 'Delete Items',
      message: `Are you sure you want to delete ${validItemIds.length} item(s)? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await api.post('/api/assessment-builder/item-bank/bulk-delete/', {
            item_ids: validItemIds
          });
          
          const data = response.data;
          
          if (data) {
            itemBankCache.invalidate(); // Clear cache for all libraries after delete
            // Refresh the current view from server so the list reflects the DB.
            invalidateAndReload({ silent: true });
            
            // Build status message with details about skipped items
            const skipped = data.skipped_items || [];
            if (skipped.length === 0) {
              setSaveStatus({ type: 'success', message: `${data.deleted_count} item(s) deleted successfully` });
            } else {
              // Separate items in use (with assessment names) from other reasons
              const inUseItems = skipped.filter(i => i.assessments?.length > 0);
              const otherItems = skipped.filter(i => !i.assessments?.length);

              // Group other items by reason
              const otherByReason = {};
              otherItems.forEach(item => {
                const reason = item.reason || 'Unknown reason';
                if (!otherByReason[reason]) otherByReason[reason] = 0;
                otherByReason[reason]++;
              });

              // Group in-use items by assessment name
              const byAssessment = {};
              inUseItems.forEach(item => {
                (item.assessments || []).forEach(name => {
                  if (!byAssessment[name]) byAssessment[name] = [];
                  byAssessment[name].push(item.code || `ID ${item.id}`);
                });
              });

              const message = (
                <div>
                  {data.deleted_count > 0 && <p>{data.deleted_count} item(s) deleted successfully.</p>}
                  <p className="mt-1">{skipped.length} item(s) could not be deleted:</p>
                  {Object.keys(byAssessment).length > 0 && (
                    <ul className="mt-1 ml-4 list-disc text-xs font-normal">
                      {Object.entries(byAssessment).map(([name, codes]) => (
                        <li key={name}>
                          <strong>{name}</strong> — {codes.length} item{codes.length !== 1 ? 's' : ''} in use
                          {user?.is_superuser && <span className="text-gray-400 ml-1">({codes.join(', ')})</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {Object.keys(otherByReason).length > 0 && (
                    <ul className="mt-1 ml-4 list-disc text-xs font-normal">
                      {Object.entries(otherByReason).map(([reason, count]) => (
                        <li key={reason}><strong>{reason}</strong> ({count} item{count !== 1 ? 's' : ''})</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
              setSaveStatus({ type: 'warning', message });
            }
            
            // Notify parent if callback provided
            if (onItemDeleted && data.deleted_items) {
              data.deleted_items.forEach(item => onItemDeleted(item.id, item, 0));
            }
          }
        } catch (error) {
          console.error('Bulk delete error:', error);
          const errorMsg = error.response?.data?.error || 'Failed to delete items';
          setSaveStatus({ type: 'error', message: errorMsg });
        }
        
        clearSelection();
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleExportCSV = () => {
    const selectedItemsData = items.filter(item => selectedItems.includes(item.id));
    
    // CSV headers - include responseOptions as JSON
    const headers = ['code', 'text', 'category', 'domain', 'responseType', 'source', 'validated', 'tags', 'responseOptions'];
    const csvContent = [
      headers.join(','),
      ...selectedItemsData.map(item => {
        // Serialize response options as JSON string
        const responseOptionsJson = item.responseOptions && item.responseOptions.length > 0
          ? JSON.stringify(item.responseOptions.map(opt => ({
              label: opt.label || opt.text || '',
              value: opt.value !== undefined ? opt.value : opt.score
            })))
          : '';
        
        return [
          item.code,
          `"${(item.text || '').replace(/"/g, '""')}"`, // Escape quotes
          item.category,
          item.domain,
          item.responseType,
          item.source,
          item.validated,
          `"${(Array.isArray(item.tags) ? item.tags : (item.tags ? item.tags.split(',').map(t => t.trim()) : [])).join(';')}"`,
          `"${responseOptionsJson.replace(/"/g, '""')}"` // Escape quotes in JSON
        ].join(',');
      })
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `item-bank-export-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    setSaveStatus({ type: 'success', message: `Exported ${selectedItems.length} items` });
  };

  // ============ CRUD Handlers ============
  // The IRT form edits `discrimination` + `c1..cN` keys, while imports, the
  // adaptive config builder, and the preview engine read `thresholds[]` /
  // `slopes[]`. Rebuild the array forms from the form fields on save so every
  // stored copy of the parameters stays in sync.
  const buildIrtSavePayload = (irtParameters) => {
    const irt = {};
    Object.entries(irtParameters || {}).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) irt[k] = v;
    });

    const discrimination = parseFloat(irt.discrimination);
    if (!isNaN(discrimination)) {
      irt.discrimination = discrimination;
      irt.slopes = [discrimination];
    } else {
      delete irt.discrimination;
      delete irt.slopes;
    }

    const cKeys = Object.keys(irt)
      .filter(k => /^c\d+$/.test(k))
      .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
    cKeys.forEach(k => {
      const num = parseFloat(irt[k]);
      if (isNaN(num)) delete irt[k];
      else irt[k] = num;
    });
    const thresholds = cKeys
      .map(k => irt[k])
      .filter(v => typeof v === 'number' && !isNaN(v) && v !== -99);
    if (thresholds.length > 0) irt.thresholds = thresholds;
    else delete irt.thresholds;

    return {
      irt_parameters: irt,
      slope: !isNaN(discrimination) ? discrimination : null,
      thresholds: thresholds.length > 0 ? thresholds : null,
    };
  };

  const handleSaveItem = async (keepOpen = false, confirmedEmptyOptions = false) => {
    // If user is creating a new category, check if it exists first
    let categoryToUse = itemForm.category;
    if (isCreatingCategory && newCategoryName.trim()) {
      // Check if category already exists (case-insensitive)
      const existingCat = categories.find(
        cat => cat.name.toLowerCase() === newCategoryName.trim().toLowerCase()
      );
      if (existingCat) {
        // Use existing category
        categoryToUse = existingCat.id;
        setItemForm(prev => ({ ...prev, category: existingCat.id }));
      } else {
        // Create new category
        const newCat = { id: newCategoryName.trim(), name: newCategoryName.trim() };
        setCategories(prev => [...prev, newCat]);
        categoryToUse = newCat.id;
        setItemForm(prev => ({ ...prev, category: newCat.id }));
      }
      setIsCreatingCategory(false);
      setNewCategoryName('');
    }
    
    // FECOA-889: Comprehensive Validation Rules
    setFormError(''); // Clear any previous error
    
    // Helper to set error and scroll to the offending field
    const setErrorAndScroll = (message, ref) => {
      setFormError(message);
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // 1. Item Code Validation
    if (!itemForm.code.trim()) {
      setErrorAndScroll('Item code is required', itemCodeRef);
      return;
    }

    const code = itemForm.code.trim();
    if (code.length > 50) {
      setErrorAndScroll('Maximum of 50 characters', itemCodeRef);
      return;
    }

    // Alphanumeric, hyphens, underscores only (no spaces or special chars)
    const codeRegex = /^[a-zA-Z0-9_-]+$/;
    if (!codeRegex.test(code)) {
      setErrorAndScroll('Item code must contain only letters, numbers, hyphens, and underscores (no spaces or special characters)', itemCodeRef);
      return;
    }

    // 2. Question Text Length Validation
    if (!itemForm.text.trim()) {
      setErrorAndScroll('Question text is required', questionTextRef);
      return;
    }

    const text = itemForm.text.trim();
    if (text.length < 3) {
      setErrorAndScroll('Question text must be at least 3 characters', questionTextRef);
      return;
    }

    if (text.length > 2000) {
      setErrorAndScroll('Question text must be 2000 characters or less', questionTextRef);
      return;
    }

    // 3. Response Options Validation (for ordinal/nominal types)
    if (['ordinal', 'nominal', 'multiple_choice'].includes(itemForm.responseType)) {
      const optionCount = itemForm.responseOptions?.length || 0;

      // Empty state: prompt for confirmation rather than blocking — lets users save
      // a draft item and fill in response options later.
      if (optionCount === 0 && !confirmedEmptyOptions) {
        setConfirmModal({
          show: true,
          title: 'Create item without response options?',
          message: 'No response options have been added. You can add them later by editing this item. Continue creating?',
          type: 'info',
          confirmText: 'Create anyway',
          onConfirm: () => {
            setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
            handleSaveItem(keepOpen, true);
          }
        });
        return;
      }

      // Partial state (1 option) is still a hard error — the user started but did
      // not finish, and that's almost always a mistake rather than an intent.
      if (optionCount === 1) {
        setErrorAndScroll(`Questions of type "${itemForm.responseType}" require at least 2 response options`, responseOptionsRef);
        return;
      }

      if (optionCount >= 2) {
        // Check for non-empty labels
        for (let i = 0; i < itemForm.responseOptions.length; i++) {
          const opt = itemForm.responseOptions[i];
          if (!opt.label || !opt.label.trim()) {
            setErrorAndScroll(`Response option ${i + 1} must have a non-empty label`, responseOptionsRef);
            return;
          }
        }

        // Duplicate response option values are intentionally allowed — a question
        // can legitimately map different labels to the same value (FECOA-1521).
      }
    }

    // FECOA-2056: a Free Text rule must be one a participant can meet.
    if (itemForm.responseType === 'text') {
      const { problems } = resolveFreeTextValidationDraft(
        itemForm.textValidation,
        itemForm.textMaxLength ?? 500,
      );
      if (problems.length > 0) {
        setErrorAndScroll(problems[0], freeTextValidationRef);
        return;
      }
    }

    const tagsArray = typeof itemForm.tags === 'string' 
      ? itemForm.tags.split(',').map(t => t.trim()).filter(t => t)
      : itemForm.tags || [];
    
    const itemData = { ...itemForm, category: categoryToUse, tags: tagsArray };

    // Build options object based on response type
    // For ordinal/nominal: use responseOptions array
    // For other types: construct type-specific configuration object
    let optionsPayload;
    switch (itemData.responseType) {
      case 'vas':
        optionsPayload = {
          type: 'vas',
          leftAnchor: itemData.vasLeftAnchor || 'No pain',
          rightAnchor: itemData.vasRightAnchor || 'Worst pain imaginable',
          minValue: 0,
          maxValue: 100
        };
        break;
      case 'numeric':
        optionsPayload = {
          type: 'numeric',
          minValue: itemData.numericMin ?? 0,
          maxValue: itemData.numericMax ?? 10,
          step: itemData.numericStep ?? 1,
          leftAnchor: itemData.numericLeftAnchor || '',
          rightAnchor: itemData.numericRightAnchor || ''
        };
        break;
      case 'text': {
        optionsPayload = {
          type: 'text',
          maxLength: itemData.textMaxLength ?? 500,
          multiline: itemData.textMultiline ?? true
        };
        // FECOA-2056: optional validation rule. Left out entirely when it
        // checks nothing, so the item behaves exactly as before.
        const { rule } = resolveFreeTextValidationDraft(
          itemData.textValidation,
          optionsPayload.maxLength,
        );
        if (rule) optionsPayload.validation = rule;
        break;
      }
      case 'boolean':
        optionsPayload = {
          type: 'boolean',
          yesLabel: itemData.booleanYesLabel || 'Yes',
          noLabel: itemData.booleanNoLabel || 'No'
        };
        break;
      case 'ordinal':
      case 'nominal':
      default:
        optionsPayload = itemData.responseOptions || [];
        break;
    }

    // Save to backend API
    try {
      if (editingItem) {
        // Update existing item via API. IRT fields ride along so edits made
        // in the IRT Parameters section persist (they were silently dropped
        // before) — assessment_id scopes the AssessmentQuestion sync.
        const irtPayload = buildIrtSavePayload(itemData.irtParameters);
        const response = await api.patch(`/api/assessment-builder/item-bank/${editingItem.id}/update/`, {
          code: itemData.code,
          text: itemData.text,
          response_type: itemData.responseType,
          response_options: optionsPayload,
          category: categoryToUse,
          domain: itemData.domain,
          tags: itemData.tags,
          validated: itemData.validated,
          source: itemData.source,
          irt_parameters: irtPayload.irt_parameters,
          irt_category: itemData.irtCategory || null,
          slope: irtPayload.slope,
          thresholds: irtPayload.thresholds,
          assessment_id: currentAssessment?.assessment_id
        });

        const data = response.data;
        if (data) {
          const updatedItem = {
            ...itemData,
            irtParameters: irtPayload.irt_parameters,
            irt_parameters: irtPayload.irt_parameters,
            id: editingItem.id,
            category: data.category_name || categoryToUse || '',
            domain: data.domain || itemData.domain || '',
            questionnaireId: data.domain || data.category_name || categoryToUse || 'uncategorized',
            responseType: data.response_type || itemData.responseType,
            responseOptions: data.response_options || optionsPayload
          };
          
          setSaveStatus({ type: 'success', message: 'Item updated' });

          // Notify parent (ScreenBuilder) so sidebar items are refreshed
          if (onItemUpdated) {
            onItemUpdated(editingItem._sidebarItemId || editingItem.id, updatedItem);
          }

          // Refetch from server so the list reflects backend-side changes
          // (text, options, derived flags like is_modified). The previous
          // optimistic merge dropped fields that didn't exist on itemForm,
          // which is why edits didn't visually update.
          invalidateAndReload();
        }
      } else {
        // Create new item via API — same IRT shape-sync as the update path.
        const irtPayload = buildIrtSavePayload(itemData.irtParameters);
        const response = await api.post('/api/assessment-builder/item-bank/create/', {
          library: itemForm.library,
          assessment_id: currentAssessment?.assessment_id,
          code: itemData.code,
          text: itemData.text,
          type: itemData.responseType,
          options: optionsPayload,
          category: itemData.category,
          domain: itemData.domain,
          tags: itemData.tags,
          irt_parameters: irtPayload.irt_parameters,
          irt_category: itemData.irtCategory || null,
          slope: irtPayload.slope,
          thresholds: irtPayload.thresholds
        });
        
        const data = response.data;
        if (data) {
          // Add the new item with the backend-assigned ID
          // Use category from API response to ensure consistency
          const itemCategory = data.item.category || categoryToUse || '';
          const itemDomain = data.item.domain || itemData.domain || '';
          const savedLibraryLevel = data.item.library_level || itemForm.library;
          const newItem = {
            ...itemData,
            id: data.item.id,
            category: itemCategory,
            domain: itemDomain,
            // CRITICAL: questionnaireId is used for grouping items in the UI
            // Must match the mapping in loadData for consistency
            questionnaireId: itemDomain || itemCategory || 'uncategorized',
            responseType: data.item.type,
            responseOptions: data.item.options,
            irtParameters: data.item.irt_parameters,
            irtCategory: data.item.irt_category,
            slope: data.item.slope,
            thresholds: data.item.thresholds,
            libraryLevel: savedLibraryLevel,
            isPublished: data.item.is_published !== false
          };
          
          if (savedLibraryLevel === activeLibrary) {
            // Refetch so the list reflects server truth (mirrors edit path).
            // The activeLibrary-switch branch below already triggers loadData
            // via the useEffect on activeLibrary, so it doesn't need this.
            setSaveStatus({ type: 'success', message: 'Item created' });
            invalidateAndReload();
          } else {
            // Item was saved to a different library - switch to show it.
            // Invalidate all caches so the destination library's loadData
            // (triggered by the activeLibrary change via useEffect) refetches
            // instead of serving a stale cached list.
            itemBankCache.invalidate();
            const libraryLabels = { assessment: 'This Assessment', study: 'Study Library', sponsor: 'Sponsor Library', vpg: 'VPG Library' };
            setSaveStatus({ type: 'success', message: `Item created in ${libraryLabels[savedLibraryLevel] || savedLibraryLevel}` });
            setTimeout(() => {
              setActiveLibrary(savedLibraryLevel);
            }, 500);
          }

          // Ensure category exists in the categories list (use functional updater to avoid stale closure dupes)
          if (newItem.category) {
            setCategories(prev =>
              prev.some(c => c.id === newItem.category || c.name.toLowerCase() === newItem.category.toLowerCase())
                ? prev
                : [...prev, { id: newItem.category, name: newItem.category }]
            );
          }
        }
      }
    } catch (error) {
      console.error('Error saving item:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save item to database';
      setFormError(errorMsg);
      return;
    }
    
    if (keepOpen) {
      // Reset form but keep modal open for adding another item
      setEditingItem(null);
      resetItemForm();
      // Keep the same category for convenience
      setItemForm(prev => ({ ...prev, category: categoryToUse }));
    } else {
      setShowItemForm(false);
      setEditingItem(null);
      resetItemForm();
    }
  };

  const handleDeleteItem = (itemId) => {
    const item = items.find(i => i.id === itemId);
    setConfirmModal({
      show: true,
      title: 'Delete Item',
      message: `Are you sure you want to delete "${item?.code}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await api.delete(`/api/assessment-builder/item-bank/${itemId}/`);
          const data = response.data;

          // Remove from selection if it was selected
          setSelectedItems(prev => prev.filter(id => String(id) !== String(itemId)));

          // Refresh from server so the list reflects what the DB now holds.
          // Silent reload avoids flashing the loading spinner over the list.
          invalidateAndReload({ silent: true });

          // Notify parent component so it can refresh its state (e.g., remove from sidebar)
          if (onItemDeleted) {
            onItemDeleted(itemId, item, data.deleted_copies || 0);
          }
          
          const message = data.deleted_copies > 0 
            ? `Item "${item?.code}" deleted (${data.deleted_copies} assessment copies also removed)`
            : `Item "${item?.code}" deleted`;
          setSaveStatus({ type: 'success', message });
        } catch (error) {
          console.error('Delete item error:', error);
          const errorMsg = error.response?.data?.error || error.message || 'Failed to delete item';
          setSaveStatus({ type: 'error', message: errorMsg });
        }
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  // Revert an edited library item to its imported baseline (text + options).
  // Visible only when the editing item has been modified and a baseline exists.
  const handleRevertToBaseline = () => {
    if (!editingItem || !editingItem.baselineSnapshot) return;
    const baseline = editingItem.baselineSnapshot;

    // Drop HTML tags and decode the entities that show up in question text
    // (rich-text editor emits &nbsp; and friends). Keep the rest verbatim.
    const cleanText = (s) => (s || '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();

    const renderOptions = (opts) => {
      if (Array.isArray(opts)) {
        if (opts.length === 0) {
          return <span className="italic text-slate-400">(none)</span>;
        }
        return (
          <ol className="space-y-1 list-decimal list-inside marker:text-slate-400">
            {opts.map((opt, idx) => {
              const label = typeof opt === 'string' ? opt : (opt?.label ?? '');
              const value = typeof opt === 'object' && opt?.value !== undefined ? ` (${opt.value})` : '';
              return <li key={idx} className="text-slate-700 dark:text-slate-200">{cleanText(label) || <span className="italic text-slate-400">(empty)</span>}<span className="text-slate-400">{value}</span></li>;
            })}
          </ol>
        );
      }
      if (opts && typeof opts === 'object') {
        const entries = Object.entries(opts);
        if (entries.length === 0) {
          return <span className="italic text-slate-400">(none)</span>;
        }
        return (
          <dl className="space-y-1">
            {entries.map(([k, v]) => (
              <div key={k} className="flex gap-2 text-slate-700 dark:text-slate-200">
                <dt className="text-slate-500 dark:text-slate-400 font-medium">{k}:</dt>
                <dd className="break-all">{String(v)}</dd>
              </div>
            ))}
          </dl>
        );
      }
      return <span className="italic text-slate-400">(none)</span>;
    };

    const currentText = cleanText(editingItem.text);
    const originalText = cleanText(baseline.text);
    const currentOptions = editingItem.responseOptions ?? editingItem.options ?? [];
    const originalOptions = baseline.options ?? [];

    const Cell = ({ children, tone }) => (
      <div className={`px-3 py-2.5 break-words text-sm ${
        tone === 'current'
          ? 'bg-slate-50 dark:bg-slate-900/40'
          : 'bg-emerald-50 dark:bg-emerald-900/20'
      }`}>
        {children}
      </div>
    );

    const SectionHeader = ({ children }) => (
      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/60 border-y border-slate-200 dark:border-slate-700">
        {children}
      </div>
    );

    const message = (
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          This restores the question text and response options to their imported baseline. Code, type, tags, and domain are not affected.
        </p>

        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Column headers */}
          <div className="grid grid-cols-2 text-[11px] font-semibold uppercase tracking-wider">
            <div className="px-3 py-2 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700">
              Current
            </div>
            <div className="px-3 py-2 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1.5">
              <RotateCcw className="w-3 h-3" />
              After revert
            </div>
          </div>

          <SectionHeader>Question text</SectionHeader>
          <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
            <Cell tone="current">
              {currentText ? `“${currentText}”` : <span className="italic text-slate-400">(empty)</span>}
            </Cell>
            <Cell tone="original">
              {originalText ? `“${originalText}”` : <span className="italic text-slate-400">(empty)</span>}
            </Cell>
          </div>

          <SectionHeader>Response options</SectionHeader>
          <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
            <Cell tone="current">{renderOptions(currentOptions)}</Cell>
            <Cell tone="original">{renderOptions(originalOptions)}</Cell>
          </div>
        </div>
      </div>
    );

    setConfirmModal({
      show: true,
      title: 'Revert to baseline?',
      message,
      type: 'info',
      confirmText: 'Revert',
      size: 'lg',
      onConfirm: async () => {
        try {
          await api.post(`/api/assessment-builder/item-bank/${editingItem.id}/revert/`);
          setSaveStatus({ type: 'success', message: 'Item reverted to baseline' });
          setShowItemForm(false);
          setEditingItem(null);
          resetItemForm();
          invalidateAndReload();
        } catch (error) {
          console.error('Revert item error:', error);
          const errorMsg = error.response?.data?.error || 'Failed to revert item';
          setSaveStatus({ type: 'error', message: errorMsg });
        }
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
      },
    });
  };

  // Edit item - populate form and open modal
  const handleEditItem = (item) => {
    setEditingItem(item);
    
    // Extract type-specific fields from options based on response type
    const options = item.responseOptions || item.options || {};
    const rawResponseType = item.responseType || item.type || 'ordinal';
    const responseType = RESPONSE_TYPE_ALIASES[rawResponseType] || rawResponseType;
    
    // Initialize type-specific defaults
    let vasLeftAnchor = '';
    let vasRightAnchor = '';
    let numericMin = 0;
    let numericMax = 10;
    let numericStep = 1;
    let numericLeftAnchor = '';
    let numericRightAnchor = '';
    let textMaxLength = 500;
    let textMultiline = true;
    let textValidation = { ...EMPTY_FREE_TEXT_VALIDATION_DRAFT };
    let booleanYesLabel = 'Yes';
    let booleanNoLabel = 'No';
    let responseOptions = [];
    
    // Parse options based on type
    if (responseType === 'vas' && options && typeof options === 'object' && !Array.isArray(options)) {
      vasLeftAnchor = options.leftAnchor || '';
      vasRightAnchor = options.rightAnchor || '';
    } else if (responseType === 'numeric' && options && typeof options === 'object' && !Array.isArray(options)) {
      numericMin = options.minValue ?? 0;
      numericMax = options.maxValue ?? 10;
      numericStep = options.step ?? 1;
      numericLeftAnchor = options.leftAnchor || '';
      numericRightAnchor = options.rightAnchor || '';
    } else if (responseType === 'text' && options && typeof options === 'object' && !Array.isArray(options)) {
      textMaxLength = options.maxLength ?? 500;
      textMultiline = options.multiline ?? true;
      textValidation = freeTextValidationToDraft(options.validation, options.maxLength);
    } else if (responseType === 'boolean' && options && typeof options === 'object' && !Array.isArray(options)) {
      booleanYesLabel = options.yesLabel || 'Yes';
      booleanNoLabel = options.noLabel || 'No';
    } else if (Array.isArray(options)) {
      responseOptions = options;
    }
    
    // IRT data arrives in several shapes depending on origin: sidebar items
    // carry snake_case `irt_parameters`, imports store `thresholds` as an
    // array (plus `slopes`/`discrimination`), while the form inputs read
    // `discrimination` + `c1..cN` keys. Normalize here so the Edit Item IRT
    // section hydrates from any of them.
    const rawIrt =
      (item.irtParameters && Object.keys(item.irtParameters).length > 0
        ? item.irtParameters
        : item.irt_parameters) || {};
    const irtForm = { ...rawIrt };
    if (irtForm.discrimination === undefined || irtForm.discrimination === null || irtForm.discrimination === '') {
      const slope = rawIrt.slope ?? (Array.isArray(rawIrt.slopes) ? rawIrt.slopes[0] : undefined) ?? item.slope;
      if (slope !== undefined && slope !== null && slope !== '') irtForm.discrimination = slope;
    }
    // Question.thresholds is a legacy comma-joined string; irt_parameters
    // stores a real array. Accept both when filling the c1..cN inputs.
    let thresholdArray = Array.isArray(rawIrt.thresholds) ? rawIrt.thresholds : null;
    if (!thresholdArray) {
      if (Array.isArray(item.thresholds)) {
        thresholdArray = item.thresholds;
      } else if (typeof item.thresholds === 'string' && item.thresholds.trim()) {
        thresholdArray = item.thresholds.split(',').map(parseFloat).filter(v => !isNaN(v));
      }
    }
    if (thresholdArray) {
      thresholdArray.forEach((t, idx) => {
        const key = `c${idx + 1}`;
        if (irtForm[key] === undefined || irtForm[key] === null || irtForm[key] === '') {
          irtForm[key] = t;
        }
      });
    }

    setItemForm({
      id: item.id || '',
      code: item.code || '',
      text: item.text || '',
      category: item.category || '',
      domain: item.domain || '',
      responseType: responseType,
      responseOptions: responseOptions,
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
      validated: item.validated || false,
      source: item.source || '',
      library: item.library || 'study',
      irtParameters: irtForm,
      irtCategory: item.irtCategory || item.irt_category || null,
      slope: item.slope || null,
      thresholds: item.thresholds || null,
      // Type-specific fields
      vasLeftAnchor,
      vasRightAnchor,
      numericMin,
      numericMax,
      numericStep,
      numericLeftAnchor,
      numericRightAnchor,
      textMaxLength,
      textMultiline,
      textValidation,
      booleanYesLabel,
      booleanNoLabel
    });
    setShowItemForm(true);
  };

  // Publish/unpublish VPG item
  const handlePublishItem = async (itemId, publish = true) => {
    try {
      const response = await api.post('/api/assessment-builder/item-bank/publish/', {
        item_id: itemId,
        action: publish ? 'publish' : 'unpublish'
      });
      
      const data = response.data;
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, isPublished: data.is_published } : item
      ));
      
      setSaveStatus({ 
        type: 'success', 
        message: `Item "${data.code}" ${publish ? 'published' : 'unpublished'} successfully` 
      });
    } catch (error) {
      setSaveStatus({ type: 'error', message: error.message });
    }
  };

  const resetItemForm = () => {
    const canCreateVPG = user?.is_superuser || userPermissions?.can_create_vpg_item;
    const defaultLibrary = (activeLibrary === 'vpg' && !canCreateVPG) ? 'sponsor' : activeLibrary;
    setItemForm({
      id: '', code: '', text: '', category: '', domain: '',
      responseType: 'ordinal', responseOptions: [], tags: '',
      source: '', validated: false,
      library: defaultLibrary, // Default to currently selected library source
      // IRT fields - set defaults based on current tab
      irtParameters: itemTypeFilter === 'irt' ? { calibrated: true } : {},
      irtCategory: itemTypeFilter === 'irt' ? 'U' : null,  // U=Uncalibrated for new IRT items
      slope: null,
      thresholds: null,
      // VAS-specific fields
      vasLeftAnchor: '',
      vasRightAnchor: '',
      // Numeric-specific fields
      numericMin: 0,
      numericMax: 10,
      numericStep: 1,
      numericLeftAnchor: '',
      numericRightAnchor: '',
      // Text-specific fields
      textMaxLength: 500,
      textMultiline: true,
      textValidation: { ...EMPTY_FREE_TEXT_VALIDATION_DRAFT },
      // Boolean-specific fields
      booleanYesLabel: 'Yes',
      booleanNoLabel: 'No'
    });
    setIsCreatingCategory(false);
    setNewCategoryName('');
    setFormError('');
  };

  const downloadTemplateCSV = () => {
    // FlexCOA Assessment format CSV template with IRT parameters
    const csvContent = `FORM_NAME,ITEM_ID,ITEM_TYPE,ITEM_TEXT,NUM_CAT,CAT_LABEL_1,CAT_LABEL_2,CAT_LABEL_3,CAT_LABEL_4,CAT_LABEL_5,CAT_CODE_1,CAT_CODE_2,CAT_CODE_3,CAT_CODE_4,CAT_CODE_5,SCORE_TYPE,SCORE_NAME,SCORE_MODEL,NUM_RCODES,NUM_FAC,A_1,C_1,C_2,C_3,C_4
MiCOAS_COG,MICOA_INST14,INST,The following questions are about your experiences over the past 14 days. In the past 14 days…,0,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99,-99
MiCOAS_COG,MICOA129,ORD,Was it difficult for you to remember the right word for something? ,5,Never ,Rarely,Sometimes,Often,Always,0,1,2,3,4,EAP,MiCOAS_COG,GRM_b,4,1,1.5,-1.93,-0.61,1.15,2.85
MiCOAS_COG,MICOA130,ORD,Was it difficult for you to have a conversation?,5,Not difficult at all,A little difficult,Somewhat difficult,Very difficult,Unable to do,0,1,2,3,3,EAP,MiCOAS_COG,GRM_b,3,1,2.91,-0.9,0.19,1.7,-99
MiCOAS_COG,MICOA132,ORD,Was it difficult for you to understand what you were reading? ,5,Not difficult at all,A little difficult,Somewhat difficult,Very difficult,Unable to do,0,1,2,3,3,EAP,MiCOAS_COG,GRM_b,3,1,2.38,-0.62,0.39,1.81,-99
MiCOAS_COG,MICOA133,ORD,Was it difficult for you to make day-to-day decisions?,5,Not difficult at all,A little difficult,Somewhat difficult,Very difficult,Unable to do,0,1,2,3,3,EAP,MiCOAS_COG,GRM_b,3,1,2.75,-0.84,0.21,1.54,-99
MiCOAS_COG,MICOA134,ORD,Was it difficult for you to carry out tasks that required many steps? ,5,Not difficult at all,A little difficult,Somewhat difficult,Very difficult,Unable to do,0,1,2,3,3,EAP,MiCOAS_COG,GRM_b,3,1,3.39,-1.02,-0.15,0.94,-99
MiCOAS_COG,MICOA135,ORD,Was it difficult for you to remember things that were important to you?,5,Never ,Rarely,Sometimes,Often,Always,0,1,2,3,3,EAP,MiCOAS_COG,GRM_b,3,1,2.73,-1.05,-0.17,1.24,-99
MiCOAS_COG,MICOA136,ORD,Was it difficult for you to do tasks that required you to concentrate? ,5,Never ,Rarely,Sometimes,Often,Always,0,1,2,3,4,EAP,MiCOAS_COG,GRM_b,4,1,2.24,-1.89,-0.8,0.71,2.35`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'flexcoa-assessment-template.csv';
    link.click();
    URL.revokeObjectURL(url);
    
    setSaveStatus({ 
      type: 'success', 
      message: 'Template CSV downloaded! Fill it out and import it back.' 
    });
  };

  const parseCSV = (csvText) => {
    const lines = [];
    let currentLine = [];
    let currentField = '';
    let insideQuotes = false;

    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      const nextChar = csvText[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          // Escaped quote
          currentField += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        // Field separator
        currentLine.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !insideQuotes) {
        // Line separator
        if (currentField || currentLine.length > 0) {
          currentLine.push(currentField.trim());
          if (currentLine.some(field => field)) {
            lines.push(currentLine);
          }
          currentLine = [];
          currentField = '';
        }
        // Skip \r\n combination
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
      } else {
        currentField += char;
      }
    }

    // Add last field and line
    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField.trim());
      if (currentLine.some(field => field)) {
        lines.push(currentLine);
      }
    }

    return lines;
  };

  // Store file for later processing (don't process immediately)
  const handleFileDrop = (file) => {
    console.log('[CSV IMPORT] handleFileDrop called with file:', file?.name, file?.type, file?.size);
    if (!file) {
      console.log('[CSV IMPORT] No file provided');
      return;
    }
    
    // Store the file and clear any previous errors
    setPendingFile(file);
    setImportErrors([]);
    console.log('[CSV IMPORT] File stored, waiting for library selection and import click');
  };
  
  // Actually process the pending file
  const processImport = async () => {
    if (!pendingFile) {
      setImportErrors(['Please select a file first']);
      return;
    }
    
    // Require library selection
    if (!importTargetLibrary) {
      setImportErrors(['Please select a target library']);
      return;
    }
    
    // Assessment- and study-scoped imports both need a current assessment as context
    if ((importTargetLibrary === 'assessment' || importTargetLibrary === 'study') && !currentAssessment?.assessment_id) {
      setImportErrors(['Please select or create an assessment first to import to this tier']);
      return;
    }

    const fileName = pendingFile.name.toLowerCase();
    const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    if (isExcelFile) {
      console.log('[CSV IMPORT] Excel file detected, sending to backend for processing');
      try {
        setImportProgress({ current: 0, total: 1, isImporting: true });
        
        const formData = new FormData();
        formData.append('file', pendingFile);
        // The /import/cdisc/ endpoint creates a new Assessment and expects Assessment.library_type
        // ('study' | 'sponsor' | 'vpg'). 'assessment' is a Question-level tier only, so map it to
        // 'study' for the purposes of the Assessment it creates.
        const cdiscLibraryType = importTargetLibrary === 'assessment' ? 'study' : importTargetLibrary;
        formData.append('library_type', cdiscLibraryType);
        if ((importTargetLibrary === 'assessment' || importTargetLibrary === 'study') && currentAssessment?.assessment_id) {
          formData.append('assessment_id', currentAssessment.assessment_id);
        }
        
        const response = await api.post('/api/assessment-builder/import/cdisc/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        console.log('[CSV IMPORT] Backend import response:', response.data);
        
        // Handle successful import - refresh item bank
        setShowImportModal(false);
        setPendingFile(null);
        setImportProgress({ current: 0, total: 0, isImporting: false });
        
        // Reload items from the target library
        setTimeout(() => {
          itemBankCache.invalidate(); // Clear all cached libraries after import
          if (activeLibrary !== importTargetLibrary) {
            setActiveLibrary(importTargetLibrary);
          } else {
            loadData();
          }
        }, 500);
        
        return;
      } catch (error) {
        console.error('[CSV IMPORT] Backend import error:', error);
        const errorMsg = error.response?.data?.error || error.message || 'Failed to import Excel file';
        setImportErrors([errorMsg]);
        setImportProgress({ current: 0, total: 0, isImporting: false });
        return;
      }
    }

    // CSV file - parse client-side
    const reader = new FileReader();
    
    reader.onerror = (error) => {
      console.error('[CSV IMPORT] FileReader error:', error);
      setImportErrors(['Failed to read file']);
    };
    
    reader.onload = async (e) => {
      console.log('[CSV IMPORT] FileReader onload triggered');
      // Clear previous errors
      setImportErrors([]);
      
      try {
        const csvText = e.target.result;
        console.log('[CSV IMPORT] CSV text length:', csvText?.length, 'First 200 chars:', csvText?.substring(0, 200));
        const lines = parseCSV(csvText);
        console.log('[CSV IMPORT] Parsed lines count:', lines.length);
        
        if (lines.length === 0) {
          setImportErrors(['CSV file is empty!']);
          return;
        }

        // Parse header
        const headers = lines[0].map(h => h.toLowerCase().trim());
        const dataLines = lines.slice(1);

        // Get column indices helper
        const getIndex = (col) => headers.indexOf(col.toLowerCase());
        
        // Detect CSV format: FlexCOA Assessment (new) vs Item Bank (legacy)
        const flexcoaHeaders = ['form_name', 'item_id', 'item_type', 'item_text'];
        const legacyHeaders = ['code', 'text', 'category', 'responsetype'];
        
        const isFlexcoaFormat = flexcoaHeaders.every(h => headers.includes(h));
        const isLegacyFormat = legacyHeaders.every(h => headers.includes(h));
        
        if (!isFlexcoaFormat && !isLegacyFormat) {
          setImportErrors([`Unrecognized CSV format. Expected FlexCOA Assessment (FORM_NAME, ITEM_ID, ITEM_TYPE, ITEM_TEXT) or legacy (code, text, category, responseType). Found: ${headers.slice(0, 5).join(', ')}...`]);
          return;
        }
        
        console.log('[CSV IMPORT] Detected format:', isFlexcoaFormat ? 'FlexCOA Assessment' : 'Legacy Item Bank');
        
        // Helper to parse boolean values more robustly
        const parseBoolean = (value) => {
          if (!value) return false;
          const cleaned = String(value).trim().toLowerCase();
          return cleaned === 'true' || cleaned === '1' || cleaned === 'yes';
        };
        
        // Helper to parse numeric values safely (handles -99 as null)
        const parseFloat = (value) => {
          if (!value || value === '-99' || value === '-99.0') return null;
          const num = Number(value);
          return isNaN(num) ? null : num;
        };
        
        const parseInt = (value, defaultVal = 0) => {
          if (!value || value === '-99') return defaultVal;
          const num = Number(value);
          return isNaN(num) ? defaultVal : Math.floor(num);
        };
        
        // Parse items
        const newItems = [];
        const errors = [];

        dataLines.forEach((line, index) => {
          if (line.length === 0 || !line[0]) return; // Skip empty lines
          
          // Helper to get and clean values from current line
          const getValue = (colName) => {
            const idx = getIndex(colName);
            if (idx === -1) return '';
            const value = line[idx];
            return value ? String(value).trim() : '';
          };
          
          try {
            let code, text, category, domain, responseType, responseOptions, validated, tagsArray, irtParameters;
            
            if (isFlexcoaFormat) {
              // FlexCOA Assessment format
              code = getValue('item_id');
              text = getValue('item_text');
              const formName = getValue('form_name');
              const itemTypeRaw = getValue('item_type');
              const itemType = itemTypeRaw.toUpperCase().replace('/', '_').replace('-', '_'); // Normalize Y/N -> Y_N
              const numCat = parseInt(getValue('num_cat'), 0);
              
              // Validate required fields
              if (!code || !text) {
                errors.push(`Row ${index + 2}: Missing required field(s): item_id or item_text`);
                return;
              }
              
              // Map ITEM_TYPE to response type
              // Supported: INST, ORD, NRS, OpenText, Nominal, Y/N, VAS
              if (itemType === 'INST') {
                responseType = 'instruction';
              } else if (itemType === 'ORD') {
                responseType = 'ordinal';
              } else if (itemType === 'NRS') {
                responseType = 'numeric'; // Numeric Rating Scale
              } else if (itemType === 'OPENTEXT' || itemType === 'TEXT') {
                responseType = 'text';
              } else if (itemType === 'NOMINAL') {
                responseType = 'nominal'; // Categorical without order
              } else if (itemType === 'Y_N' || itemType === 'YN' || itemType === 'YES_NO' || itemType === 'BOOL') {
                responseType = 'boolean';
              } else if (itemType === 'VAS') {
                responseType = 'vas'; // Visual Analog Scale
              } else if (itemType === 'NUM') {
                responseType = 'numeric';
              } else {
                responseType = numCat > 0 ? 'ordinal' : 'text';
              }
              
              // Build response options from CAT_LABEL and CAT_CODE columns (dynamic based on numCat)
              responseOptions = [];
              const maxCategories = Math.max(numCat, 10); // Check up to numCat or 10 columns
              for (let i = 1; i <= maxCategories; i++) {
                const label = getValue(`cat_label_${i}`);
                const codeVal = getValue(`cat_code_${i}`);
                if (label && label !== '-99' && codeVal !== '-99') {
                  responseOptions.push({
                    id: `opt_${Date.now()}_${index}_${i}`,
                    order: i,
                    label: label.trim(),
                    value: codeVal || String(i - 1)
                  });
                }
              }
              
              // Clear options for types that don't use them
              if (['INST', 'OPENTEXT', 'TEXT', 'VAS'].includes(itemType)) {
                responseOptions = [];
              }
              
              // Use score_name as category, form_name as domain
              const scoreName = getValue('score_name');
              category = (scoreName && scoreName !== '-99') ? scoreName : (formName || 'Imported');
              domain = formName || '';
              
              // Parse IRT/scoring parameters
              const scoreType = getValue('score_type'); // NONE, SUM, MEAN, EAP, MAP
              const scoreModel = getValue('score_model'); // 1PL, 2PL, 3PL, NOM, GRM_b, GRM_c
              const numFac = parseInt(getValue('num_fac'), 0);
              const scoreFac = parseInt(getValue('score_fac'), 0);
              
              // Parse slopes (A parameters) - dynamic based on num_fac
              const slopes = [];
              const maxFactors = Math.max(numFac, 5);
              for (let i = 1; i <= maxFactors; i++) {
                const slope = parseFloat(getValue(`a_${i}`));
                if (slope !== null) slopes.push(slope);
              }
              const discrimination = slopes.length > 0 ? slopes[0] : null;
              
              // Parse intercepts (C parameters) - dynamic, one less than num_cat
              const thresholds = [];
              const maxIntercepts = Math.max(numCat - 1, 10);
              for (let i = 1; i <= maxIntercepts; i++) {
                const threshold = parseFloat(getValue(`c_${i}`));
                if (threshold !== null) thresholds.push(threshold);
              }
              
              // Build IRT parameters (only if we have actual IRT/scoring data)
              irtParameters = {};
              const hasIrtData = (scoreType && !['', '-99', 'NONE'].includes(scoreType)) || discrimination !== null || thresholds.length > 0;
              
              if (hasIrtData) {
                irtParameters = {
                  score_type: (scoreType && scoreType !== '-99') ? scoreType : null,
                  score_name: (scoreName && scoreName !== '-99') ? scoreName : null,
                  score_model: (scoreModel && scoreModel !== '-99') ? scoreModel : null,
                  num_response_codes: parseInt(getValue('num_rcodes'), 0) || null,
                  num_factors: numFac || null,
                  score_factor: scoreFac || null,
                  slopes: slopes.length > 0 ? slopes : null,
                  discrimination: discrimination, // Primary slope (A_1)
                  thresholds: thresholds.length > 0 ? thresholds : null,
                };
                // Remove null values
                irtParameters = Object.fromEntries(
                  Object.entries(irtParameters).filter(([_, v]) => v !== null)
                );
              }
              
              validated = hasIrtData; // Items with IRT params are validated
              tagsArray = (scoreName && scoreName !== '-99') ? [scoreName] : [];
              
            } else {
              // Legacy Item Bank format
              code = getValue('code');
              text = getValue('text');
              category = getValue('category');
              responseType = getValue('responsetype');

              // Validate required fields
              if (!code || !text || !category || !responseType) {
                const missing = [];
                if (!code) missing.push('code');
                if (!text) missing.push('text');
                if (!category) missing.push('category');
                if (!responseType) missing.push('responseType');
                errors.push(`Row ${index + 2}: Missing required field(s): ${missing.join(', ')}`);
                return;
              }
              
              domain = getValue('domain');
              validated = parseBoolean(getValue('validated'));
              
              // Parse tags from CSV (comma-separated string) into array
              const tagsString = getValue('tags');
              tagsArray = tagsString ? tagsString.split(',').map(t => t.trim()).filter(t => t) : [];
              
              // Parse responseOptions from JSON string
              const responseOptionsString = getValue('responseoptions');
              responseOptions = [];
              if (responseOptionsString) {
                try {
                  const parsed = JSON.parse(responseOptionsString);
                  responseOptions = Array.isArray(parsed) ? parsed.map((opt, optIdx) => ({
                    id: `opt_${Date.now()}_${index}_${optIdx}`,
                    label: opt.label || opt.text || '',
                    value: opt.value !== undefined ? opt.value : optIdx,
                    order: optIdx
                  })) : [];
                } catch (e) {
                  console.warn(`Row ${index + 2}: Could not parse responseOptions JSON:`, e.message);
                }
              }
              
              irtParameters = {};
            }

            // Check for duplicate codes
            if (items.find(item => item.code === code) || newItems.find(item => item.code === code)) {
              errors.push(`Row ${index + 2}: Duplicate code "${code}"`);
              return;
            }

            const item = {
              id: `imported_${Date.now()}_${index}`,
              code: code,
              text: text,
              category: category,
              domain: domain,
              questionnaireId: category || 'uncategorized',  // Group by category for item sets
              response_type: responseType,
              responseType: responseType,
              source: getValue('source') || '',
              validated: validated,
              tags: tagsArray,
              responseOptions: responseOptions,
              item_bank_id: null,
              question_type: responseType,
              irtParameters: irtParameters,
              // For VPG library: mark as pending review
              isPublished: importTargetLibrary !== 'vpg',  // Auto-publish for non-VPG, pending for VPG
              libraryLevel: importTargetLibrary  // Track which library this was imported to
            };

            newItems.push(item);
          } catch (error) {
            errors.push(`Row ${index + 2}: ${error.message}`);
          }
        });

        // Show results
        if (newItems.length === 0) {
          setImportErrors(errors.length > 0 ? errors : ['No valid items found in CSV']);
        } else {
          // Save items to database via API
          const savedItems = [];
          const saveErrors = [];
          
          // Initialize progress
          setImportProgress({ current: 0, total: newItems.length, isImporting: true });
          
          for (let i = 0; i < newItems.length; i++) {
            const item = newItems[i];
            // Update progress
            setImportProgress(prev => ({ ...prev, current: i + 1 }));
            try {
              const response = await api.post('/api/assessment-builder/item-bank/create/', {
                code: item.code,
                text: item.text,
                category: item.category,
                domain: item.domain,
                type: item.responseType,
                options: item.responseOptions,
                source: item.source,
                validated: item.validated,
                tags: item.tags,
                library: importTargetLibrary,
                assessment_id: importTargetLibrary === 'assessment' ? currentAssessment?.assessment_id : null,
                irt_parameters: item.irtParameters || {},
                slope: item.irtParameters?.discrimination || null,
                thresholds: item.irtParameters?.thresholds || null
              });
              
              const data = response.data;
              savedItems.push({
                ...item,
                id: data.item.id,  // Use real database ID
                isPublished: data.item.is_published !== false,
                libraryLevel: importTargetLibrary
              });
            } catch (err) {
              if (err.response?.status === 409) {
                // Duplicate item - skip but note it
                saveErrors.push(`${item.code}: Duplicate item exists`);
              } else {
                const errorMsg = err.response?.data?.error || err.message || 'Failed to save';
                saveErrors.push(`${item.code}: ${errorMsg}`);
              }
            }
          }
          
          // Add saved items to state only if they belong to the currently viewed library
          // This prevents items from appearing in the wrong library view temporarily
          if (savedItems.length > 0) {
            if (activeLibrary === importTargetLibrary) {
              // Same library - add items directly
              const updatedItems = [...items, ...savedItems];
              setItems(updatedItems);
            } else {
              // Different library - switch to it (loadData will fetch items)
              setTimeout(() => {
                setActiveLibrary(importTargetLibrary);
              }, 500);
            }
          }
          
          // Show import summary/review screen instead of auto-closing
          setShowImportModal(false);
          setPendingFile(null); // Clear pending file after import
          setImportSummary({
            show: true,
            items: savedItems,
            errors: [...errors, ...saveErrors],
            selectedIds: new Set(savedItems.map(item => item.id)) // Pre-select all by default
          });
          
          console.log(`[ITEM BANK IMPORT] Saved ${savedItems.length} items to database:`, savedItems.map(i => i.id));
          
          // Reset progress
          setImportProgress({ current: 0, total: 0, isImporting: false });
        }
      } catch (error) {
        console.error('CSV parsing error:', error);
        setImportErrors([`CSV parsing failed: ${error.message}`]);
        setImportProgress({ current: 0, total: 0, isImporting: false });
      }
    };
    
    // Actually start reading the file
    console.log('[CSV IMPORT] Starting file read...');
    reader.readAsText(pendingFile);
  };

  // ============ VPG Library Handlers ============
  const fetchQuestionnaires = async () => {
    setVpgLoading(true);
    setVpgError(''); // Clear previous errors
    try {
      const response = await api.get('/api/assessment-builder/item-bank/pre-validated/');
      const data = response.data;
      console.log('VPG questionnaires:', data);
      setQuestionnaires(data.questionnaires || []);
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      setVpgError('Failed to load VPG library');
    } finally {
      setVpgLoading(false);
    }
  };

  const fetchQuestionnaireQuestions = async (questionnaireId) => {
    setVpgLoading(true);
    setVpgError('');
    try {
      const response = await api.get(`/api/assessment-builder/item-bank/pre-validated/${questionnaireId}/`);
      const data = response.data;
      setVpgQuestions(data.questions || []);
      setSelectedQuestionnaire(data.questionnaire);
    } catch (error) {
      console.error('Error fetching questionnaire questions:', error);
      setVpgError('Failed to load questionnaire questions');
    } finally {
      setVpgLoading(false);
    }
  };

  const searchVPGLibrary = async (query = '', domain = '') => {
    setVpgLoading(true);
    setVpgError('');
    try {
      const params = new URLSearchParams();
      if (query) params.append('search', query);
      if (domain) params.append('domain', domain);
      
      const response = await api.get(`/api/assessment-builder/item-bank/vpg-library/?${params}`);
      const data = response.data;
      setVpgQuestions(data.questions || []);
    } catch (error) {
      console.error('Error searching VPG library:', error);
      setVpgError('Search failed');
    } finally {
      setVpgLoading(false);
    }
  };

  const importVPGQuestions = async () => {
    if (selectedVPGQuestions.length === 0) {
      setVpgError('No questions selected');
      return;
    }
    setVpgError('');

    setVpgLoading(true);
    try {
      const response = await api.post('/api/assessment-builder/item-bank/import-vpg/', {
        question_ids: selectedVPGQuestions
      });
      
      const data = response.data;
      
      setSaveStatus({ 
        type: 'success', 
        message: data.message || `Imported ${data.imported_count} questions`
      });
      
      // Switch to sponsor library where VPG items are imported
      setActiveLibrary('sponsor');
      
      // Close VPG library modal and reset selection
      setShowVPGLibrary(false);
      setSelectedVPGQuestions([]);
      setSelectedQuestionnaire(null);
      setVpgQuestions([]);
    } catch (error) {
      console.error('Error importing VPG questions:', error);
      setVpgError(error.message || 'Import failed');
    } finally {
      setVpgLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileDrop(files[0]);
    }
  };

  const getCategoryCounts = () => {
    const counts = {};
    items.forEach(item => {
      const category = item.category || 'Uncategorized';
      counts[category] = (counts[category] || 0) + 1;
    });
    return counts;
  };

  // Helper: Check if an item is IRT-based
  const isIrtItem = (item) => {
    // Check for actual IRT parameter values — not just any non-empty object.
    // { calibrated: true } is a creation artifact, not a real IRT item.
    const irt = item.irtParameters || {};
    const hasSlope = irt.slope !== undefined && irt.slope !== null;
    const hasDiscrimination = irt.discrimination !== undefined && irt.discrimination !== null;
    const hasThresholds = irt.thresholds !== undefined && irt.thresholds !== null &&
                          Array.isArray(irt.thresholds) && irt.thresholds.length > 0;
    const hasIrtParams = hasSlope || hasDiscrimination || hasThresholds;
    // Check if category is 'C' (Calibrated)
    const isCalibrated = item.irtCategory === 'C';
    return hasIrtParams || isCalibrated;
  };

  // Group items by questionnaire/item set
  const getItemSets = (itemsList) => {
    const sets = {};
    itemsList.forEach(item => {
      const setId = item.questionnaireId || 'uncategorized';
      if (!sets[setId]) {
        sets[setId] = {
          id: setId,
          name: setId === 'uncategorized' ? 'Uncategorized Items' : setId.toUpperCase().replace(/_/g, ' '),
          items: [],
          domain: item.domain || '',
          validated: false
        };
      }
      sets[setId].items.push(item);
      if (item.validated) sets[setId].validated = true;
    });
    return Object.values(sets).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Toggle item set expansion
  const toggleSetExpanded = (setId) => {
    setExpandedSets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(setId)) {
        newSet.delete(setId);
      } else {
        newSet.add(setId);
      }
      return newSet;
    });
  };

  // Helper to check if an item is already in the assessment
  const isItemInAssessment = (item) => {
    return assessmentItems.some(existing => 
      existing.id === item.id || 
      existing.item_bank_id === item.id ||
      (existing.code && item.code && existing.code === item.code)
    );
  };

  // Helper to check if all items in a set are already in the assessment
  const areAllItemsInAssessment = (itemSet) => {
    if (!itemSet?.items?.length) return false;
    return itemSet.items.every(item => isItemInAssessment(item));
  };

  // Get count of items not yet in assessment for a set
  const getNewItemsCount = (itemSet) => {
    if (!itemSet?.items?.length) return 0;
    return itemSet.items.filter(item => !isItemInAssessment(item)).length;
  };

  // Map a serialized item-bank Question (backend shape) into the frontend
  // shape used by the rest of this component and ScreenBuilder's onAddItem.
  const mapServerItemToFrontend = (item) => ({
    id: item.id,
    code: item.code,
    text: item.text,
    category: item.category || '',
    domain: item.domain || '',
    questionnaireId: item.questionnaire_id || item.domain || item.category || 'uncategorized',
    responseType: item.response_type || item.type,
    responseOptions: item.options || [],
    source: item.source || '',
    validated: item.validated || false,
    tags: item.tags || [],
    irtParameters: item.irt_parameters || {},
    irtCategory: item.adaptive_category || null,
    slope: item.slope || null,
    thresholds: item.thresholds || null,
    isPublished: item.is_published !== false,
    libraryLevel: item.library_level || 'assessment',
    isModified: !!item.is_modified,
    baselineSnapshot: item.baseline_snapshot || null,
  });

  // Clone source items into the assessment library so that anything added
  // to the assessment is its own assessment-scoped copy. Returns the cloned
  // items (mapped to frontend shape) on success, or null on failure (caller
  // should abort and surface an error).
  const cloneItemsForAssessment = async (sourceItems) => {
    if (!currentAssessment?.assessment_uuid && !currentAssessment?.assessment_id) {
      return sourceItems; // No assessment context — nothing to clone into.
    }
    if (!sourceItems || sourceItems.length === 0) return [];
    try {
      const result = await AssessmentBuilderAPI.importItemsToAssessment(
        currentAssessment.assessment_uuid || currentAssessment.assessment_id,
        sourceItems.map(i => i.id)
      );
      const mapping = result.mapping || {};
      const serverItems = result.items || [];
      const byId = new Map(serverItems.map(it => [it.id, it]));
      // Preserve caller order using the mapping.
      const ordered = sourceItems
        .map(src => byId.get(mapping[String(src.id)]))
        .filter(Boolean)
        .map(mapServerItemToFrontend);
      // Refresh the local "This Assessment" view next time it opens so the
      // new clones show up.
      itemBankCache.invalidate();
      return ordered;
    } catch (error) {
      console.error('[ITEM BANK] Clone-to-assessment failed:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to add items to assessment';
      setSaveStatus({ type: 'error', message: errorMsg });
      return null;
    }
  };

  // Import entire item set (batch import)
  const handleImportSet = async (itemSet) => {
    if (!currentAssessment) {
      setSaveStatus({ type: 'error', message: 'No assessment selected' });
      return;
    }
    const cloned = await cloneItemsForAssessment(itemSet.items);
    if (!cloned) return;
    // Pass array to onAddItem for batch processing (silently skips duplicates)
    const itemsArray = cloned.map(item => {
      const irtParams = item.irtParameters || {};
      const mappedIrtParams = {
        ...irtParams,
        slope: irtParams.discrimination || item.slope || null,
        thresholds: irtParams.thresholds || item.thresholds || null
      };
      return {
        type: 'question',
        itemData: { ...item, irt_parameters: mappedIrtParams },
        responseOptions: item.responseOptions
      };
    });
    onAddItem(itemsArray);
    setSaveStatus({
      type: 'success',
      message: `Added ${cloned.length} items from "${itemSet.name}" to assessment`
    });
  };

  if (!isOpen) return null;

  const counts = getCategoryCounts();
  
  // Separate items into standard and IRT-based
  const standardItems = items.filter(item => !isIrtItem(item));
  const irtItems = items.filter(item => isIrtItem(item));
  
  // Get item sets for the selected type
  const currentItems = itemTypeFilter === 'irt' ? irtItems : standardItems;
  
  // For VPG library, separate published and pending items (only show VPG-level items as pending)
  const publishedItems = activeLibrary === 'vpg' 
    ? currentItems.filter(item => item.isPublished) 
    : currentItems;
  const pendingItems = activeLibrary === 'vpg' 
    ? currentItems.filter(item => !item.isPublished && item.libraryLevel === 'vpg') 
    : [];
  
  const itemSets = getItemSets(publishedItems);
  
  // Apply search filter and selected set filter to item sets
  let filteredItemSets = itemSets;
  
  // Filter by selected set from sidebar
  if (selectedSet) {
    filteredItemSets = filteredItemSets.filter(set => set.id === selectedSet);
  }
  
  // Apply search filter
  if (searchQuery.trim()) {
    filteredItemSets = filteredItemSets.map(set => ({
      ...set,
      items: set.items.filter(item => {
        const query = searchQuery.toLowerCase();
        const tags = Array.isArray(item.tags) ? item.tags : [];
        return item.text?.toLowerCase().includes(query) ||
          item.code?.toLowerCase().includes(query) ||
          tags.some(tag => tag.toLowerCase().includes(query));
      })
    })).filter(set => set.items.length > 0);
  }
  
  console.log('[ITEM BANK RENDER] Standard:', standardItems.length, 'IRT:', irtItems.length, 'Sets:', filteredItemSets.length);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 dark:bg-black/70 p-4">
      <div className="rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10 w-[90vw] h-[85vh] max-w-7xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-5">
            <div className="flex items-start gap-2.5">
              <Package className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" />
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Item Library</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {viewMode === 'browse' ? 'Browse and select questions' : 'Manage categories and questions'}
                </p>
              </div>
            </div>

            {/* Library Source Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200 dark:border-gray-700">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Source:</label>
              <select
                value={activeLibrary}
                onChange={(e) => setActiveLibrary(e.target.value)}
                className="input h-9 w-auto pr-9"
              >
                <option value="assessment">This Assessment</option>
                <option value="study">Study Library</option>
                <option value="sponsor">Sponsor Library</option>
                <option value="vpg">VPG Library (Global)</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="segmented">
              <button
                onClick={() => { setViewMode('browse'); clearSelection(); }}
                className={`segmented-item ${
                  viewMode === 'browse'
                    ? 'segmented-item-active'
                    : ''
                }`}
              >
                <Search className="h-4 w-4" />
                Browse
              </button>
              {(activeLibrary !== 'vpg' || canManageVPGLibrary) && (
                <button
                  onClick={() => { setViewMode('manage'); clearSelection(); }}
                  className={`segmented-item ${
                    viewMode === 'manage'
                      ? 'segmented-item-active'
                      : ''
                  }`}
                >
                  <Settings className="h-4 w-4" />
                  Manage
                </button>
              )}
            </div>

            {/* Import Button */}
            <button
              onClick={() => {
                setImportTargetLibrary(''); // Clear selection to force user to choose
                setShowImportModal(true);
              }}
              className="btn btn-secondary"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>

            <button
              onClick={onClose}
              className="-mr-2 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        {/* Status Message */}
        {saveStatus.message && (
          <div className={`mx-6 mt-4 flex items-start gap-2 rounded-lg border px-4 py-3 ${
            saveStatus.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
            : saveStatus.type === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'
            : 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'
          }`}>
            <div className="mt-0.5 flex-shrink-0">
              {saveStatus.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            </div>
            <div className="text-sm font-medium flex-1">{saveStatus.message}</div>
            <button onClick={() => setSaveStatus({ type: '', message: '' })} className="ml-auto flex-shrink-0 rounded-md p-1 transition-colors hover:bg-black/5 dark:hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {selectedItems.length > 0 && (
          <div className="border-b border-gray-100 bg-gray-50/70 px-6 py-2.5 dark:border-gray-700 dark:bg-gray-900/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={clearSelection}
                  className="btn btn-ghost btn-sm"
                >
                  Clear
                </button>
              </div>
              <div className="flex gap-2">
                {currentAssessment && (
                  <button
                    onClick={handleBulkAddToAssessment}
                    className="btn btn-primary"
                  >
                    <Plus className="h-4 w-4" />
                    Add to Assessment
                  </button>
                )}
                <button
                  onClick={handleExportCSV}
                  className="btn btn-secondary"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
                {/* FECOA-902: Hide bulk delete for VPG items if user lacks permission */}
                {(activeLibrary !== 'vpg' || canDeleteVPGItems) && (
                  <button
                    onClick={handleBulkDelete}
                    className="btn btn-danger-soft"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Item Type Sidebar */}
          <div className="w-72 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col overflow-hidden">
            {/* Item Type Tabs */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700">
              <div className="segmented flex w-full">
                <button
                  onClick={() => setItemTypeFilter('standard')}
                  className={`segmented-item flex-1 justify-center ${
                    itemTypeFilter === 'standard'
                      ? 'segmented-item-active'
                      : ''
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Items
                  <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">
                    {standardItems.length}
                  </span>
                </button>
                {currentAssessment?.builder_metadata?.enable_irt_parameters && (
                <button
                  onClick={() => setItemTypeFilter('irt')}
                  className={`segmented-item flex-1 justify-center ${
                    itemTypeFilter === 'irt'
                      ? 'segmented-item-active'
                      : ''
                  }`}
                >
                  <Zap className="h-4 w-4" />
                  IRT
                  <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">
                    {irtItems.length}
                  </span>
                </button>
                )}
              </div>
            </div>

            {/* Item Sets List */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Pending Review Section - VPG only */}
              {activeLibrary === 'vpg' && pendingItems.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Pending Review</h3>
                    </div>
                    <span className="badge badge-info tabular-nums">
                      {pendingItems.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {pendingItems.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{item.code}</span>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 line-clamp-2"
                              dangerouslySetInnerHTML={{ __html: item.text?.substring(0, 80) || '' }}
                            />
                          </div>
                          {canPublishVPGItems && (
                            <button
                              onClick={() => handlePublishItem(item.id, true)}
                              className="btn btn-secondary btn-sm ml-2"
                              title="Publish this item"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Publish
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-3 border-b border-gray-100 dark:border-gray-700 pb-2">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Item Sets</h3>
                </div>
                <div className="flex items-center gap-2">
                  {selectedSet && (
                    <button
                      onClick={() => setSelectedSet(null)}
                      className="text-xs font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      Show All
                    </button>
                  )}
                  <span className="badge badge-gray tabular-nums">
                    {itemSets.length}
                  </span>
                </div>
              </div>

              {filteredItemSets.length === 0 ? (
                <div className="rounded-lg bg-gray-50 p-6 text-center dark:bg-gray-900/40">
                  <Package className="mx-auto mb-3 h-6 w-6 text-gray-400" />
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    No {itemTypeFilter === 'irt' ? 'IRT-based' : 'standard'} item sets found
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {activeLibrary === 'assessment'
                      ? 'Items created for this assessment will appear here'
                      : activeLibrary === 'study'
                      ? 'Items shared across all assessments in this study will appear here'
                      : activeLibrary === 'sponsor'
                      ? 'Items shared across all studies for your organization will appear here'
                      : 'Validated global instruments from the VPG library will appear here'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredItemSets.map((itemSet) => (
                    <div
                      key={itemSet.id}
                      className={`overflow-hidden rounded-lg border bg-white transition-colors duration-150 dark:bg-gray-800 ${
                        selectedSet === itemSet.id
                          ? 'border-primary-600 dark:border-primary-400'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                      }`}
                    >
                      <button
                        onClick={() => {
                          toggleSetExpanded(itemSet.id);
                          setSelectedSet(selectedSet === itemSet.id ? null : itemSet.id);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-3 text-left group transition-colors duration-150 ${
                          selectedSet === itemSet.id ? 'bg-primary-50/40 dark:bg-primary-400/10' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {itemSet.name}
                              </span>
                              {itemSet.validated && (
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                              )}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {itemSet.items.length} question{itemSet.items.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${
                          expandedSets.has(itemSet.id) ? 'rotate-90' : ''
                        }`} />
                      </button>

                      {expandedSets.has(itemSet.id) && (
                        <div className="border-t border-gray-100 bg-gray-50/70 px-3 py-3 space-y-2 dark:border-gray-700 dark:bg-gray-900/40">
                          {/* Import All Button - hidden when all items already in assessment */}
                          {currentAssessment && !areAllItemsInAssessment(itemSet) && (
                            <button
                              onClick={() => handleImportSet(itemSet)}
                              className="btn btn-primary btn-sm w-full"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Import All ({getNewItemsCount(itemSet)} new)
                            </button>
                          )}
                          {/* Show "All Added" indicator when all items are in assessment */}
                          {currentAssessment && areAllItemsInAssessment(itemSet) && (
                            <div className="flex w-full items-center justify-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-xs font-medium text-gray-500 dark:bg-gray-700/60 dark:text-gray-400">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              All items added
                            </div>
                          )}

                          {/* Individual Items */}
                          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                            {itemSet.items.map((item) => {
                              const alreadyAdded = isItemInAssessment(item);
                              return (
                              <div
                                key={item.id}
                                className="flex items-center justify-between py-2 text-xs"
                              >
                                <div className="flex-1 min-w-0 mr-2">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono text-gray-500 dark:text-gray-400">{item.code}</span>
                                    {alreadyAdded && (
                                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                                    )}
                                  </div>
                                  <p className="text-gray-700 truncate dark:text-gray-300" title={htmlToPlainText(item.text)}>
                                    {htmlToPlainText(item.text).substring(0, 50)}...
                                  </p>
                                </div>
                                {currentAssessment && !alreadyAdded && (
                                  <button
                                    onClick={async () => {
                                      const cloned = await cloneItemsForAssessment([item]);
                                      if (!cloned || cloned.length === 0) return;
                                      const cloneItem = cloned[0];
                                      const irtParams = cloneItem.irtParameters || {};
                                      const mappedIrtParams = {
                                        ...irtParams,
                                        slope: irtParams.discrimination || cloneItem.slope || null,
                                        thresholds: irtParams.thresholds || cloneItem.thresholds || null
                                      };
                                      onAddItem({
                                        type: 'question',
                                        itemData: { ...cloneItem, irt_parameters: mappedIrtParams },
                                        responseOptions: cloneItem.responseOptions
                                      });
                                      setSaveStatus({ type: 'success', message: `Added "${cloneItem.code}" to assessment` });
                                    }}
                                    className="btn btn-ghost btn-sm btn-icon flex-shrink-0"
                                    title="Add to Assessment"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            );})}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Items Detail Panel */}
          <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
            {/* Search + Actions Bar */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 flex gap-3">
              <div className="flex-1 relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items by text, code, or tags..."
                  className="input h-9 pl-9"
                />
              </div>

              {(activeLibrary !== 'vpg' || canEditVPGItems) && (
                <button
                  onClick={() => { setEditingItem(null); resetItemForm(); setShowItemForm(true); }}
                  className="btn btn-primary"
                >
                  <Plus className="h-4 w-4" />
                  New Item
                </button>
              )}
            </div>

            {/* Items Summary */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent mb-3"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading items...</p>
                </div>
              ) : filteredItemSets.length === 0 ? (
                <div className="card p-8 text-center">
                  <Package className="mx-auto mb-3 h-6 w-6 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">No item sets found</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {activeLibrary === 'assessment'
                      ? 'Items created for this assessment will appear here. Use "+ New Item" or "Import" to add items.'
                      : activeLibrary === 'study'
                      ? 'Items shared across all assessments in this study will appear here.'
                      : activeLibrary === 'sponsor'
                      ? 'Items shared across all studies for your organization will appear here.'
                      : 'Validated global instruments from the VPG library will appear here.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Compact header with item count and Select All */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
                          {/* Show set name directly when only 1 set exists */}
                          {filteredItemSets.length === 1 && filteredItemSets[0].id !== 'uncategorized'
                            ? filteredItemSets[0].name
                            : (itemTypeFilter === 'irt' ? 'IRT Items' : 'Standard Items')
                          }
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {currentItems.length} item{currentItems.length !== 1 ? 's' : ''}
                          {filteredItemSets.length > 1 && ` in ${filteredItemSets.length} sets`}
                          {filteredItemSets.length === 1 && filteredItemSets[0].validated && (
                            <span className="ml-2 inline-flex items-center text-green-600 dark:text-green-400">
                              <CheckCircle2 className="w-3 h-3 mr-0.5" /> Validated
                            </span>
                          )}
                        </p>
                      </div>
                      {/* Import All button for single-set view */}
                      {filteredItemSets.length === 1 && currentAssessment && !areAllItemsInAssessment(filteredItemSets[0]) && (
                        <button
                          onClick={() => handleImportSet(filteredItemSets[0])}
                          className="btn btn-primary btn-sm ml-3"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Import All ({getNewItemsCount(filteredItemSets[0])})
                        </button>
                      )}
                      {filteredItemSets.length === 1 && currentAssessment && areAllItemsInAssessment(filteredItemSets[0]) && (
                        <span className="badge badge-gray ml-3">
                          <CheckCircle2 className="h-3 w-3" />
                          All added
                        </span>
                      )}
                    </div>
                    {/* Select All / Deselect All */}
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={currentItems.length > 0 && selectedItems.length === currentItems.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems(currentItems.map(item => item.id));
                            } else {
                              setSelectedItems([]);
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {selectedItems.length === currentItems.length && currentItems.length > 0 ? 'Deselect All' : 'Select All'}
                        </span>
                      </label>
                      {selectedItems.length > 0 && (
                        <span className="badge badge-brand tabular-nums">
                          {selectedItems.length} selected
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Simplified display when only 1 item set exists - show items directly without nested card */}
                  {filteredItemSets.length === 1 ? (
                    <div className="card overflow-hidden">
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {filteredItemSets[0].items.map((item) => (
                          <div
                            key={item.id}
                            className={`flex items-start px-4 py-3 transition-colors duration-150 ${
                              selectedItems.includes(item.id)
                                ? 'bg-primary-50/40 dark:bg-primary-400/10'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                            }`}
                          >
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              className="w-4 h-4 mt-1 mr-3 rounded border-gray-300 cursor-pointer flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0 mr-2">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{item.code}</span>
                                {item.isModified && (
                                  <span
                                    title="This item has been edited since import"
                                    className="badge badge-gray"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                    Modified
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm mt-1 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: item.text?.substring(0, 100) || '' }}
                              />
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => setPreviewItem(item)}
                                className="btn btn-ghost btn-sm btn-icon"
                                title="Preview item"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {currentAssessment && (
                                <button
                                  onClick={async () => {
                                    const cloned = await cloneItemsForAssessment([item]);
                                    if (!cloned || cloned.length === 0) return;
                                    const cloneItem = cloned[0];
                                    const irtParams = cloneItem.irtParameters || {};
                                    const mappedIrtParams = {
                                      ...irtParams,
                                      slope: irtParams.discrimination || cloneItem.slope || null,
                                      thresholds: irtParams.thresholds || cloneItem.thresholds || null
                                    };
                                    onAddItem({
                                      type: 'question',
                                      itemData: { ...cloneItem, irt_parameters: mappedIrtParams },
                                      responseOptions: cloneItem.responseOptions
                                    });
                                    setSaveStatus({ type: 'success', message: `Added "${cloneItem.code}"` });
                                  }}
                                  className="btn btn-ghost btn-sm btn-icon"
                                  title="Add to assessment"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              )}
                              {viewMode === 'manage' && (
                                <>
                                  {/* VPG items are editable only with the VPG curation permission */}
                                  {(activeLibrary !== 'vpg' || canEditVPGItems) && (
                                    <button
                                      onClick={() => handleEditItem(item)}
                                      className="btn btn-ghost btn-sm btn-icon"
                                      title="Edit item"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* FECOA-902: Hide delete for VPG items if user lacks permission */}
                                  {(activeLibrary !== 'vpg' || canDeleteVPGItems) && (
                                    <button
                                      onClick={() => handleDeleteItem(item.id)}
                                      className="btn btn-danger-soft btn-sm btn-icon"
                                      title="Delete item"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                  /* Multiple item sets - show grid layout */
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredItemSets.map((itemSet) => (
                      <div 
                        key={itemSet.id}
                        className="card overflow-hidden"
                      >
                        <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" />
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{itemSet.name}</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {itemSet.items.length} question{itemSet.items.length !== 1 ? 's' : ''}
                                  {itemSet.validated && (
                                    <span className="ml-2 inline-flex items-center text-green-600 dark:text-green-400">
                                      <CheckCircle2 className="w-3 h-3 mr-0.5" /> Validated
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {currentAssessment && !areAllItemsInAssessment(itemSet) && (
                              <button
                                onClick={() => handleImportSet(itemSet)}
                                className="btn btn-secondary btn-sm"
                              >
                                <Download className="w-3.5 h-3.5" />
                                Import All ({getNewItemsCount(itemSet)})
                              </button>
                            )}
                            {currentAssessment && areAllItemsInAssessment(itemSet) && (
                              <span className="badge badge-gray">
                                <CheckCircle2 className="h-3 w-3" />
                                Added
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="max-h-52 overflow-y-auto">
                          <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {itemSet.items.slice(0, 5).map((item) => (
                              <div
                                key={item.id}
                                className={`flex items-start px-4 py-3 transition-colors duration-150 ${
                                  selectedItems.includes(item.id)
                                    ? 'bg-primary-50/40 dark:bg-primary-400/10'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                                }`}
                              >
                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={selectedItems.includes(item.id)}
                                  onChange={() => toggleItemSelection(item.id)}
                                  className="w-4 h-4 mt-1 mr-3 rounded border-gray-300 cursor-pointer flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0 mr-2">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{item.code}</span>
                                    {item.isModified && (
                                      <span
                                        title="This item has been edited since import"
                                        className="badge badge-gray"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                        Modified
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-gray-700 dark:text-gray-300 text-sm mt-1 line-clamp-2"
                                    dangerouslySetInnerHTML={{ __html: item.text?.substring(0, 100) || '' }}
                                  />
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => setPreviewItem(item)}
                                    className="btn btn-ghost btn-sm btn-icon"
                                    title="Preview item"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {currentAssessment && !isItemInAssessment(item) && (
                                    <button
                                      onClick={async () => {
                                        const cloned = await cloneItemsForAssessment([item]);
                                        if (!cloned || cloned.length === 0) return;
                                        const cloneItem = cloned[0];
                                        const irtParams = cloneItem.irtParameters || {};
                                        const mappedIrtParams = {
                                          ...irtParams,
                                          slope: irtParams.discrimination || cloneItem.slope || null,
                                          thresholds: irtParams.thresholds || cloneItem.thresholds || null
                                        };
                                        onAddItem({
                                          type: 'question',
                                          itemData: { ...cloneItem, irt_parameters: mappedIrtParams },
                                          responseOptions: cloneItem.responseOptions
                                        });
                                        setSaveStatus({ type: 'success', message: `Added "${cloneItem.code}"` });
                                      }}
                                      className="btn btn-ghost btn-sm btn-icon"
                                      title="Add to assessment"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  )}
                                  {currentAssessment && isItemInAssessment(item) && (
                                    <span className="p-2 text-green-500" title="Already in assessment">
                                      <CheckCircle2 className="w-4 h-4" />
                                    </span>
                                  )}
                                  {viewMode === 'manage' && (
                                    <>
                                      {/* VPG items are editable only with the VPG curation permission */}
                                      {(activeLibrary !== 'vpg' || canEditVPGItems) && (
                                        <button
                                          onClick={() => handleEditItem(item)}
                                          className="btn btn-ghost btn-sm btn-icon"
                                          title="Edit item"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </button>
                                      )}
                                      {/* FECOA-902: Hide delete for VPG items if user lacks permission */}
                                      {(activeLibrary !== 'vpg' || canDeleteVPGItems) && (
                                        <button
                                          onClick={() => handleDeleteItem(item.id)}
                                          className="btn btn-danger-soft btn-sm btn-icon"
                                          title="Delete item"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                            {itemSet.items.length > 5 && (
                              <p className="py-2 text-center text-xs text-gray-500 dark:text-gray-400">
                                + {itemSet.items.length - 5} more items
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Item Form Modal */}
      {showItemForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 dark:bg-black/70">
          <div className="rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10 w-full max-w-3xl max-h-[85vh] overflow-y-auto m-4">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4 px-6 py-4">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">{editingItem ? 'Edit Item' : 'Create New Item'}</h3>
                <button onClick={() => { setShowItemForm(false); setEditingItem(null); setFormError(''); }} className="-mr-2 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200">
                  <X className="h-[18px] w-[18px]" />
                </button>
              </div>

              {/* Form Error Display - inside sticky header so it's always visible */}
              {formError && (
                <div className="mx-6 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{formError}</span>
                  <button onClick={() => setFormError('')} className="ml-auto flex-shrink-0 rounded-md p-1 transition-colors hover:bg-red-100 dark:hover:bg-red-500/20">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Save Location Section */}
              {!editingItem && (
                <div className="border-b border-gray-100 pb-5 dark:border-gray-700">
                  <label className="label">Save Item To:</label>
                  <div className={`grid gap-2 ${(user?.is_superuser || userPermissions?.can_create_vpg_item) ? 'grid-cols-4' : 'grid-cols-3'}`}>
                    <button
                      type="button"
                      onClick={() => setItemForm({ ...itemForm, library: 'assessment' })}
                      className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                        itemForm.library === 'assessment'
                          ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium">This Assessment</div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Only this assessment</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemForm({ ...itemForm, library: 'study' })}
                      className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                        itemForm.library === 'study'
                          ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium">Study Library</div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">All assessments in study</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemForm({ ...itemForm, library: 'sponsor' })}
                      className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                        itemForm.library === 'sponsor'
                          ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium">Sponsor Library</div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Share across studies</div>
                    </button>
                    {(user?.is_superuser || userPermissions?.can_create_vpg_item) && (
                      <button
                        type="button"
                        onClick={() => setItemForm({ ...itemForm, library: 'vpg' })}
                        className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                          itemForm.library === 'vpg'
                            ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="font-medium">VPG Global</div>
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Admin only</div>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Required Fields Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">1</div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Basic Information</h4>
                  <span className="text-xs text-red-500 dark:text-red-400">* Required</span>
                </div>

                <div className="grid gap-4 grid-cols-2">
                  <div ref={itemCodeRef}>
                    <label className="label">Item Code *</label>
                    {/* FECOA-1562: lock the code only once it actually has a value.
                        An item imported without a code stays editable so the user
                        can assign one and save. */}
                    {(() => {
                      const codeLocked = !!editingItem && !!editingItem.code;
                      return (<>
                        <input
                          type="text"
                          value={itemForm.code}
                          onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })}
                          readOnly={codeLocked}
                          className={`input ${
                            codeLocked ? 'cursor-not-allowed bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400' : ''
                          }`}
                          placeholder="e.g., CUSTOM-1"
                          maxLength={20}
                          title={codeLocked ? 'Item code is locked after the first save' : undefined}
                        />
                        <p className="hint">
                          {codeLocked
                            ? 'Locked after first save.'
                            : 'Letters, numbers, hyphens, underscores only. Max 20 chars.'}
                        </p>
                      </>);
                    })()}
                  </div>
                  <div>
                    <label className="label">Category</label>
                    {!isCreatingCategory ? (
                      <>
                        <select 
                          value={itemForm.category} 
                          onChange={(e) => {
                            if (e.target.value === '__create_new__') {
                              setIsCreatingCategory(true);
                              setNewCategoryName('');
                              setItemForm({ ...itemForm, category: '' });
                            } else {
                              setItemForm({ ...itemForm, category: e.target.value });
                            }
                          }} 
                          className="input"
                        >
                          <option value="">Select category (optional)</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                          <option value="__create_new__" className="text-primary-600 font-medium">+ Create New Category</option>
                        </select>
                        <p className="hint">Organize by topic or domain</p>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newCategoryName.trim()) {
                                const trimmed = newCategoryName.trim();
                                setCategories(prev => {
                                  const existing = prev.find(cat => cat.name.toLowerCase() === trimmed.toLowerCase());
                                  if (existing) {
                                    setItemForm(f => ({ ...f, category: existing.id }));
                                    return prev;
                                  }
                                  const newCat = { id: trimmed, name: trimmed };
                                  setItemForm(f => ({ ...f, category: newCat.id }));
                                  return [...prev, newCat];
                                });
                                setIsCreatingCategory(false);
                                setNewCategoryName('');
                              } else if (e.key === 'Escape') {
                                setIsCreatingCategory(false);
                                setNewCategoryName('');
                              }
                            }}
                            placeholder="Enter new category name"
                            className="input h-9 flex-1"
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              if (newCategoryName.trim()) {
                                const trimmed = newCategoryName.trim();
                                setCategories(prev => {
                                  const existing = prev.find(cat => cat.name.toLowerCase() === trimmed.toLowerCase());
                                  if (existing) {
                                    setItemForm(f => ({ ...f, category: existing.id }));
                                    return prev;
                                  }
                                  const newCat = { id: trimmed, name: trimmed };
                                  setItemForm(f => ({ ...f, category: newCat.id }));
                                  return [...prev, newCat];
                                });
                                setIsCreatingCategory(false);
                                setNewCategoryName('');
                              }
                            }}
                            className="btn btn-primary"
                            disabled={!newCategoryName.trim()}
                          >
                            Add
                          </button>
                          <button
                            onClick={() => {
                              setIsCreatingCategory(false);
                              setNewCategoryName('');
                            }}
                            className="btn btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Press Enter to add or Escape to cancel</p>
                      </div>
                    )}
                  </div>
                </div>

                <div ref={questionTextRef}>
                  <label className="label">
                    {itemForm.responseType === 'instruction' ? 'Instruction Text *' : 'Question Text *'}
                  </label>
                  <RichTextEditor
                    value={itemForm.text}
                    onChange={(html) => setItemForm({ ...itemForm, text: html })}
                    placeholder={itemForm.responseType === 'instruction'
                      ? 'Enter the instruction text for participants to follow...'
                      : 'Enter the question participants will see...'}
                    rows={3}
                  />
                  <p className="hint">Min 3 chars, max 2000 chars. Bold/italics available.</p>
                </div>
              </div>

              {/* Response Configuration Section */}
              <div className="space-y-4" ref={responseOptionsRef}>
                <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">2</div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Response Configuration</h4>
                </div>

                <div>
                  <label className="label">Response Type *</label>
                  <select
                    value={itemForm.responseType}
                    onChange={(e) => setItemForm({ ...itemForm, responseType: e.target.value })}
                    className="input"
                  >
                    {responseTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <p className="hint">How participants answer this question</p>
                </div>

                {/* Conditional Response Configuration based on Response Type */}
                
                {/* VAS Configuration */}
                {itemForm.responseType === 'vas' && (
                  <div className="space-y-3 rounded-md bg-gray-50 p-4 dark:bg-gray-900/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      <Activity className="h-4 w-4 text-gray-400" />
                      Visual Analog Scale Settings
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Left Anchor (Min)</label>
                        <input
                          type="text"
                          value={itemForm.vasLeftAnchor}
                          onChange={(e) => setItemForm({ ...itemForm, vasLeftAnchor: e.target.value })}
                          placeholder="e.g., No pain"
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Right Anchor (Max)</label>
                        <input
                          type="text"
                          value={itemForm.vasRightAnchor}
                          onChange={(e) => setItemForm({ ...itemForm, vasRightAnchor: e.target.value })}
                          placeholder="e.g., Worst pain imaginable"
                          className="input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Slider from 0-100 with custom anchor labels</p>
                  </div>
                )}

                {/* Numeric Rating Scale Configuration */}
                {itemForm.responseType === 'numeric' && (
                  <div className="space-y-3 rounded-md bg-gray-50 p-4 dark:bg-gray-900/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      <Hash className="h-4 w-4 text-gray-400" />
                      Numeric Rating Scale Settings
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="label">Min Value</label>
                        <NumericInput
                          value={itemForm.numericMin}
                          fallback={0}
                          min={0}
                          max={10}
                          onChange={(val) => setItemForm({ ...itemForm, numericMin: val })}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Max Value</label>
                        <NumericInput
                          value={itemForm.numericMax}
                          fallback={10}
                          min={0}
                          max={10}
                          onChange={(val) => setItemForm({ ...itemForm, numericMax: val })}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Step</label>
                        <NumericInput
                          value={itemForm.numericStep}
                          fallback={1}
                          min={1}
                          onChange={(val) => setItemForm({ ...itemForm, numericStep: val })}
                          className="input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Left Anchor Label</label>
                        <input
                          type="text"
                          value={itemForm.numericLeftAnchor}
                          onChange={(e) => setItemForm({ ...itemForm, numericLeftAnchor: e.target.value })}
                          placeholder="e.g., No pain"
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Right Anchor Label</label>
                        <input
                          type="text"
                          value={itemForm.numericRightAnchor}
                          onChange={(e) => setItemForm({ ...itemForm, numericRightAnchor: e.target.value })}
                          placeholder="e.g., Worst pain"
                          className="input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Clickable number buttons from {itemForm.numericMin} to {itemForm.numericMax}</p>
                  </div>
                )}

                {/* Text Configuration */}
                {itemForm.responseType === 'text' && (
                  <div className="space-y-3 rounded-md bg-gray-50 p-4 dark:bg-gray-900/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      <FileText className="h-4 w-4 text-gray-400" />
                      Free Text Settings
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Max Length</label>
                        <NumericInput
                          value={itemForm.textMaxLength}
                          fallback={500}
                          min={1}
                          max={10000}
                          onChange={(val) => setItemForm({ ...itemForm, textMaxLength: val })}
                          className="input"
                        />
                      </div>
                      <div className="flex items-center pt-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={itemForm.textMultiline}
                            onChange={(e) => setItemForm({ ...itemForm, textMultiline: e.target.checked })}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Multi-line (textarea)</span>
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {itemForm.textMultiline ? 'Large text area for extended responses' : 'Single-line text input'}
                    </p>
                    <div ref={freeTextValidationRef}>
                      <FreeTextValidationSettings
                        draft={itemForm.textValidation}
                        maxLength={itemForm.textMaxLength ?? 500}
                        onChange={(textValidation) => setItemForm((prev) => ({ ...prev, textValidation }))}
                      />
                    </div>
                  </div>
                )}

                {/* Boolean Configuration */}
                {itemForm.responseType === 'boolean' && (
                  <div className="space-y-3 rounded-md bg-gray-50 p-4 dark:bg-gray-900/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      <Check className="h-4 w-4 text-gray-400" />
                      Yes/No Settings
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Yes Label</label>
                        <input
                          type="text"
                          value={itemForm.booleanYesLabel}
                          onChange={(e) => setItemForm({ ...itemForm, booleanYesLabel: e.target.value })}
                          placeholder="Yes"
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">No Label</label>
                        <input
                          type="text"
                          value={itemForm.booleanNoLabel}
                          onChange={(e) => setItemForm({ ...itemForm, booleanNoLabel: e.target.value })}
                          placeholder="No"
                          className="input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Two-option binary choice</p>
                  </div>
                )}

                {/* Ordinal/Nominal - Show Response Options Table */}
                {(itemForm.responseType === 'ordinal' || itemForm.responseType === 'nominal') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Response Options</label>
                      <div className="flex items-center gap-2">
                        <select
                          onChange={(e) => {
                            if (e.target.value && RESPONSE_OPTION_PRESETS[e.target.value]) {
                              setItemForm({
                                ...itemForm,
                                responseOptions: [...RESPONSE_OPTION_PRESETS[e.target.value]]
                              });
                            }
                            e.target.value = ''; // Reset after selection
                          }}
                          className="input h-8 w-auto cursor-pointer px-2.5 pr-8 text-[13px]"
                          defaultValue=""
                        >
                          <option value="" disabled className="text-gray-500 dark:bg-gray-800 dark:text-gray-400">Quick Fill...</option>
                          {Object.entries(PRESET_LABELS).map(([key, label]) => (
                            <option key={key} value={key} className="text-gray-900 dark:text-gray-100 dark:bg-gray-800">{label}</option>
                          ))}
                        </select>
                        <button 
                          onClick={() => {
                            setItemForm({
                              ...itemForm,
                              responseOptions: [
                                ...itemForm.responseOptions, 
                                { order: itemForm.responseOptions.length + 1, label: '', value: itemForm.responseOptions.length }
                              ]
                            });
                          }}
                          className="btn btn-secondary btn-sm"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add Option
                        </button>
                      </div>
                    </div>

                    <div className="card overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr>
                            <th className="th-cell w-12 px-3">#</th>
                            <th className="th-cell px-3">Label</th>
                            <th className="th-cell w-24 px-3">Value</th>
                            <th className="th-cell w-12 px-3"></th>
                          </tr>
                        </thead>
                      <tbody className="bg-white dark:bg-gray-800">
                        {itemForm.responseOptions.map((option, index) => (
                          <tr key={index} className="border-t border-gray-100 dark:border-gray-700">
                            <td className="px-3 py-2 tabular-nums text-gray-500 dark:text-gray-400">{option.order}</td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={option.label}
                                onChange={(e) => {
                                  const updated = [...itemForm.responseOptions];
                                  updated[index].label = e.target.value;
                                  setItemForm({ ...itemForm, responseOptions: updated });
                                }}
                                onPaste={(e) => {
                                  const pasted = e.clipboardData.getData('text');
                                  const lines = pasted.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                                  if (lines.length <= 1) return;
                                  e.preventDefault();
                                  const before = itemForm.responseOptions.slice(0, index);
                                  const after = itemForm.responseOptions.slice(index + 1);
                                  const inserted = lines.map((label) => ({ label }));
                                  const merged = [...before, ...inserted, ...after].map((opt, i) => ({
                                    ...opt,
                                    order: i + 1,
                                    value: i,
                                  }));
                                  setItemForm({ ...itemForm, responseOptions: merged });
                                }}
                                className="input h-8 px-2.5 text-[13px]"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input 
                                type="number" 
                                value={option.value} 
                                onChange={(e) => {
                                  const updated = [...itemForm.responseOptions];
                                  updated[index].value = Number(e.target.value);
                                  setItemForm({ ...itemForm, responseOptions: updated });
                                }}
                                className="input h-8 px-2.5 text-[13px]" 
                              />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button 
                                onClick={() => {
                                  const updated = itemForm.responseOptions
                                    .filter((_, i) => i !== index)
                                    .map((opt, i) => ({ ...opt, order: i + 1 }));
                                  setItemForm({ ...itemForm, responseOptions: updated });
                                }}
                                className="btn btn-danger-soft btn-sm btn-icon"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {itemForm.responseOptions.length === 0 && (
                          <tr>
                            <td colSpan="4" className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                              No options. Click "Add Option" or use Quick Fill.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {itemForm.responseType === 'ordinal'
                      ? 'Ordered scale (e.g., Likert). Values determine scoring order.'
                      : 'Unordered categories. Each option is independent.'}
                  </p>
                </div>
                )}
              </div>

              {/* Optional Details Section */}
              {itemForm.responseType !== 'instruction' && (
              <div className="space-y-4 rounded-md bg-gray-50 p-4 dark:bg-gray-900/40">
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">3</div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Optional Details</h4>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Not required</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Domain</label>
                    <input 
                      type="text" 
                      value={itemForm.domain} 
                      onChange={(e) => setItemForm({ ...itemForm, domain: e.target.value.toUpperCase() })} 
                      className="input" 
                      placeholder="e.g., PAIN" 
                    />
                    <p className="hint">CDISC domain code</p>
                  </div>
                  <div>
                    <label className="label">Source</label>
                    <input 
                      type="text" 
                      value={itemForm.source} 
                      onChange={(e) => setItemForm({ ...itemForm, source: e.target.value })} 
                      className="input" 
                      placeholder="e.g., PHQ-9" 
                    />
                    <p className="hint">Published instrument name</p>
                  </div>
                </div>

                <div>
                  <label className="label">Tags (comma-separated)</label>
                  <input 
                    type="text" 
                    value={itemForm.tags} 
                    onChange={(e) => setItemForm({ ...itemForm, tags: e.target.value })} 
                    className="input" 
                    placeholder="e.g., depression, validated, screening" 
                  />
                  <p className="hint">Helps with searching and filtering</p>
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={itemForm.validated}
                      onChange={(e) => setItemForm({ ...itemForm, validated: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Mark as Validated</span>
                  </label>
                  <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
                    Check this if the item is from a clinically validated instrument (e.g., PHQ-9, GAD-7)
                  </p>
                </div>
              </div>
              )}

              {/* IRT Parameters Section - gated on the per-assessment "Enable IRT Parameters"
                  toggle in Manage Assessment > Settings. Hidden by default. */}
              {itemForm.responseType !== 'instruction' && currentAssessment?.builder_metadata?.enable_irt_parameters && (
                <div className="space-y-4 rounded-md bg-gray-50 p-4 dark:bg-gray-900/40">
                  <div className="flex items-center gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">4</div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">IRT Parameters</h4>
                    <Zap className="h-4 w-4 text-gray-400" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">For adaptive testing</span>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="label">Score Type</label>
                      <select
                        value={itemForm.irtParameters?.score_type || ''}
                        onChange={(e) => setItemForm({ 
                          ...itemForm, 
                          irtParameters: { ...itemForm.irtParameters, score_type: e.target.value }
                        })}
                        className="input"
                      >
                        <option value="">Select...</option>
                        <option value="EAP">EAP (Expected A Posteriori)</option>
                        <option value="MAP">MAP (Maximum A Posteriori)</option>
                        <option value="MLE">MLE (Maximum Likelihood)</option>
                        <option value="SUM">SUM (Simple Sum)</option>
                      </select>
                      <p className="hint">Scoring algorithm</p>
                    </div>
                    <div>
                      <label className="label">Score Name</label>
                      <input
                        type="text"
                        value={itemForm.irtParameters?.score_name || ''}
                        onChange={(e) => setItemForm({ 
                          ...itemForm, 
                          irtParameters: { ...itemForm.irtParameters, score_name: e.target.value }
                        })}
                        placeholder="e.g., MiCOAS_COG"
                        className="input"
                      />
                      <p className="hint">Scale/subscale name</p>
                    </div>
                    <div>
                      <label className="label">Score Model</label>
                      <select
                        value={itemForm.irtParameters?.score_model || ''}
                        onChange={(e) => setItemForm({ 
                          ...itemForm, 
                          irtParameters: { ...itemForm.irtParameters, score_model: e.target.value }
                        })}
                        className="input"
                      >
                        <option value="">Select...</option>
                        <option value="GRM">GRM (Graded Response Model)</option>
                        <option value="GRM_b">GRM_b (GRM, b-parameterization)</option>
                        <option value="GRM_c">GRM_c (GRM, c-parameterization)</option>
                        <option value="2PL">2PL (Two-Parameter Logistic)</option>
                        <option value="3PL">3PL (Three-Parameter Logistic)</option>
                      </select>
                      <p className="hint">IRT model type</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Discrimination (a)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={itemForm.irtParameters?.discrimination ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || val === '.' || /^\d*\.?\d*$/.test(val)) {
                            setItemForm({ 
                              ...itemForm, 
                              irtParameters: { ...itemForm.irtParameters, discrimination: val }
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const num = parseFloat(e.target.value);
                          if (!isNaN(num)) {
                            setItemForm({ 
                              ...itemForm, 
                              irtParameters: { ...itemForm.irtParameters, discrimination: num }
                            });
                          }
                        }}
                        placeholder="e.g., 2.5"
                        className="input"
                      />
                      <p className="hint">Item discrimination parameter (slope)</p>
                    </div>
                    <div>
                      <label className="label">IRT Category</label>
                      <select
                        value={itemForm.irtCategory || ''}
                        onChange={(e) => setItemForm({ ...itemForm, irtCategory: e.target.value })}
                        className="input"
                      >
                        <option value="">Select...</option>
                        <option value="C">C - Calibrated</option>
                        <option value="U">U - Uncalibrated</option>
                        <option value="E">E - Excluded from scoring</option>
                        <option value="S">S - Screening item</option>
                      </select>
                      <p className="hint">Calibration status</p>
                    </div>
                  </div>

                  <div>
                    <label className="label">
                      Difficulty Thresholds (b/c parameters)
                      {itemForm.responseOptions?.length > 0 && (
                        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                          {itemForm.responseOptions.length} options → {itemForm.responseOptions.length - 1} thresholds needed
                        </span>
                      )}
                    </label>
                    <div className="grid grid-cols-4 gap-3">
                      {Array.from({ length: Math.max(itemForm.responseOptions?.length - 1 || 0, 4) }, (_, idx) => idx + 1).map((i) => {
                        const numOptions = itemForm.responseOptions?.length || 0;
                        const thresholdsNeeded = numOptions > 0 ? numOptions - 1 : 4;
                        const isUnused = numOptions > 0 && i > thresholdsNeeded;
                        
                        return (
                          <div key={i}>
                            <label className={`mb-1 block text-xs font-medium ${isUnused ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                              c{i} {isUnused && '(unused)'}
                            </label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={isUnused ? '-99' : (itemForm.irtParameters?.[`c${i}`] ?? '')}
                              disabled={isUnused}
                              onChange={(e) => {
                                if (isUnused) return;
                                const val = e.target.value;
                                if (val === '' || val === '-' || val === '.' || val === '-.' || /^-?\d*\.?\d*$/.test(val)) {
                                  setItemForm({ 
                                    ...itemForm, 
                                    irtParameters: { 
                                      ...itemForm.irtParameters, 
                                      [`c${i}`]: val
                                    }
                                  });
                                }
                              }}
                              onBlur={(e) => {
                                if (isUnused) return;
                                const val = e.target.value;
                                const num = parseFloat(val);
                                if (!isNaN(num)) {
                                  setItemForm({ 
                                    ...itemForm, 
                                    irtParameters: { 
                                      ...itemForm.irtParameters, 
                                      [`c${i}`]: num
                                    }
                                  });
                                }
                              }}
                              placeholder={`c${i}`}
                              className={`input tabular-nums ${
                                isUnused
                                  ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                                  : ''
                              }`}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Thresholds = response options - 1. Unused thresholds auto-set to -99.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900">
              {editingItem && editingItem.isModified && editingItem.baselineSnapshot && (
                <button
                  onClick={handleRevertToBaseline}
                  title="Restore question text and response options to their imported values"
                  className="btn btn-secondary mr-auto"
                >
                  <RotateCcw className="h-4 w-4" />
                  Revert to baseline
                </button>
              )}
              <button
                onClick={() => { setShowItemForm(false); setEditingItem(null); resetItemForm(); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              {!editingItem && (
                <button
                  onClick={() => handleSaveItem(true)}
                  className="btn btn-secondary"
                >
                  <Save className="h-4 w-4" />
                  Create & Add Another
                </button>
              )}
              <button
                onClick={() => handleSaveItem(false)}
                className="btn btn-primary"
              >
                <Save className="w-4 h-4" />
                {editingItem ? 'Update Item' : 'Create Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Assessment Modal - Enhanced Summary/Confirmation Screen */}
      {showAddToAssessmentModal && currentAssessment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 dark:bg-black/70">
          <div className="rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10 w-full max-w-3xl max-h-[85vh] overflow-hidden m-4">
            {/* Header */}
            <div className="border-b border-gray-100 px-6 py-4 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Confirm Questions to Add</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Review and confirm {selectedItems.length} selected question{selectedItems.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => setShowAddToAssessmentModal(false)}
                  className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                  <X className="h-[18px] w-[18px]" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5 overflow-y-auto max-h-[calc(85vh-200px)]">
              {/* Summary Stats */}
              <div className="mb-6 grid grid-cols-3 divide-x divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                <div className="px-4 py-3">
                  <div className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">{selectedItems.length}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Questions Selected</div>
                </div>
                <div className="px-4 py-3">
                  <div className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                    {new Set(items.filter(item => selectedItems.includes(item.id)).map(i => i.category)).size}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Categories</div>
                </div>
                <div className="px-4 py-3">
                  <div className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                    {items.filter(item => selectedItems.includes(item.id) && isIrtItem(item)).length}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">IRT Items</div>
                </div>
              </div>

              {/* Selected Items List - Interactive */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Selected Questions</h4>
                  <button
                    onClick={clearSelection}
                    className="text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Clear All
                  </button>
                </div>
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="max-h-64 overflow-y-auto">
                    {items.filter(item => selectedItems.includes(item.id)).map((item, index) => (
                      <div
                        key={item.id}
                        className="group flex items-start gap-3 border-b border-gray-100 p-3 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {index + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs font-semibold text-gray-900 dark:text-gray-100">{item.code}</span>
                              {isIrtItem(item) && (
                                <span className="badge badge-gray">
                                  <Zap className="w-3 h-3" />
                                  IRT
                                </span>
                              )}
                              <span className="badge badge-gray">
                                {item.category}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2 dark:text-gray-300">
                              {htmlToPlainText(item.text)}
                            </p>
                            {item.source && (
                              <p className="hint">Source: {item.source}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedItems(selectedItems.filter(id => id !== item.id));
                          }}
                          className="btn btn-danger-soft btn-sm btn-icon flex-shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          title="Remove from selection"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {selectedItems.length === 0 && (
                    <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      <AlertCircle className="mx-auto mb-2 h-6 w-6 text-gray-400" />
                      <p>No questions selected</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Configuration Info */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                  <div>
                    <h4 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">What happens next:</h4>
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                      <li>• You&apos;ll choose a layout template for each question type</li>
                      <li>• Screens will be auto-built with items grouped and positioned</li>
                      <li>• Questions will also appear in the Elements sidebar for further editing</li>
                      <li>• You can customize layouts and positioning anytime in the Screen Builder</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedItems.length > 0 ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                    Ready to add {selectedItems.length} question{selectedItems.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span className="text-gray-400">Select at least one question</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddToAssessmentModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (selectedItems.length === 0) return;
                    // Get the full item objects for the selected IDs
                    const selectedFullItems = items.filter(item => selectedItems.includes(item.id));
                    // Clone into the assessment library so subsequent steps
                    // (auto-build, sidebar add) operate on assessment-scoped IDs.
                    const cloned = await cloneItemsForAssessment(selectedFullItems);
                    if (!cloned || cloned.length === 0) return;
                    setPendingSelectedItems(cloned);
                    setShowAddToAssessmentModal(false);
                    setShowConfigureLayout(true);
                  }}
                  disabled={selectedItems.length === 0}
                  className="btn btn-primary"
                >
                  <Plus className="h-4 w-4" />
                  Add {selectedItems.length} Question{selectedItems.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 dark:bg-black/70"
          onDragOver={(e) => e.preventDefault()}
        >
          <div className="overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10 w-full max-w-2xl m-4">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Import Questions</h3>
              <button
                onClick={() => { if (!importProgress.isImporting) { setShowImportModal(false); setImportErrors([]); setPendingFile(null); }}}
                disabled={importProgress.isImporting}
                className={`-mr-2 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors ${importProgress.isImporting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200'}`}
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>

            {/* Import Target Library Selection */}
            <div className="px-6 pb-4 pt-5">
              <label className="label">Import To:</label>
              <div className={`grid gap-2 ${(user?.is_superuser || userPermissions?.can_create_vpg_item) ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <button
                  type="button"
                  onClick={() => currentAssessment && setImportTargetLibrary('assessment')}
                  disabled={!currentAssessment}
                  className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                    importTargetLibrary === 'assessment'
                      ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                      : !currentAssessment
                        ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-700 opacity-60 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium">This Assessment</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{currentAssessment ? 'Only this assessment' : 'No assessment selected'}</div>
                </button>
                <button
                  type="button"
                  onClick={() => currentAssessment && setImportTargetLibrary('study')}
                  disabled={!currentAssessment}
                  className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                    importTargetLibrary === 'study'
                      ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                      : !currentAssessment
                        ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-700 opacity-60 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium">Study Library</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{currentAssessment ? 'All assessments in study' : 'No assessment selected'}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setImportTargetLibrary('sponsor')}
                  className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                    importTargetLibrary === 'sponsor'
                      ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium">Sponsor Library</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Share across studies</div>
                </button>
                {(user?.is_superuser || userPermissions?.can_create_vpg_item) && (
                  <button
                    type="button"
                    onClick={() => setImportTargetLibrary('vpg')}
                    className={`rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                      importTargetLibrary === 'vpg'
                        ? 'border-primary-500 bg-primary-50/40 text-gray-900 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:text-gray-50 dark:ring-primary-400'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="font-medium">VPG Global</div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Admin only</div>
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4 px-6 pb-5">
              <div
                onClick={() => {
                  if (importProgress.isImporting) return;
                  console.log('[CSV IMPORT] Drop zone clicked, fileInputRef:', fileInputRef.current);
                  fileInputRef.current?.click();
                }}
                onDragOver={importProgress.isImporting ? undefined : handleDragOver}
                onDragEnter={importProgress.isImporting ? undefined : handleDragOver}
                onDragLeave={importProgress.isImporting ? undefined : handleDragLeave}
                onDrop={importProgress.isImporting ? undefined : handleDrop}
                className={`rounded-lg border border-dashed p-8 text-center transition-colors ${
                  importProgress.isImporting
                    ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-50 dark:border-gray-700 dark:bg-gray-900/40'
                    : isDragging
                      ? 'cursor-pointer border-primary-500 bg-primary-50/40 dark:border-primary-400 dark:bg-primary-400/10'
                      : 'cursor-pointer border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
                }`}
              >
                {pendingFile ? (
                  // File ready state
                  <>
                    <FileText className="mx-auto mb-3 h-6 w-6 text-gray-400" />
                    <p className="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">{pendingFile.name}</p>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      {(pendingFile.size / 1024).toFixed(1)} KB • Ready to import
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="text-xs text-gray-500 underline hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    >
                      Remove file
                    </button>
                  </>
                ) : (
                  // Empty state
                  <>
                    <Upload className={`mx-auto mb-3 h-6 w-6 transition-colors ${
                      isDragging ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'
                    }`} />
                    <p className="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {isDragging ? 'Drop file here!' : 'Drop CSV file here or click to browse'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Supported formats: CSV, Excel</p>
                  </>
                )}
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept=".csv,.xlsx,.xls" 
                  className="hidden" 
                  onChange={(e) => {
                    console.log('[CSV IMPORT] File input onChange triggered, files:', e.target.files);
                    const file = e.target.files[0];
                    console.log('[CSV IMPORT] Selected file:', file?.name, file?.type, file?.size);
                    if (file) {
                      handleFileDrop(file);
                    } else {
                      console.log('[CSV IMPORT] No file selected');
                    }
                  }}
                />
              </div>

              {/* File Ready - Import Button */}
              {pendingFile && !importProgress.isImporting && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-primary-800">File ready to import</p>
                      <p className="text-xs text-primary-600">
                        {importTargetLibrary === 'assessment' && currentAssessment
                          ? `Importing to: This Assessment`
                          : importTargetLibrary === 'study' && currentAssessment
                            ? `Importing to: Study Library`
                            : importTargetLibrary === 'sponsor'
                              ? 'Importing to: Sponsor Library'
                              : importTargetLibrary === 'vpg'
                                ? 'Importing to: VPG Global'
                              : 'Select a library above'}
                      </p>
                    </div>
                    <button
                      onClick={processImport}
                      disabled={!importTargetLibrary || ((importTargetLibrary === 'assessment' || importTargetLibrary === 'study') && !currentAssessment?.assessment_id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                        !importTargetLibrary || ((importTargetLibrary === 'assessment' || importTargetLibrary === 'study') && !currentAssessment?.assessment_id)
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      <Upload className="w-4 h-4" />
                      Import Now
                    </button>
                  </div>
                </div>
              )}

              {/* Import Progress Bar */}
              {importProgress.isImporting && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-primary-700">
                      Importing items...
                    </span>
                    <span className="text-sm text-primary-600">
                      {importProgress.current} / {importProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-primary-100 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-primary-600 mt-2 text-center">
                    {Math.round((importProgress.current / importProgress.total) * 100)}% complete
                  </p>
                </div>
              )}

              {/* Import Errors Display */}
              {importErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-800 mb-1">Import Errors</h4>
                      <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {importErrors.map((error, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <span className="text-red-400">•</span>
                            <span>{error}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => setImportErrors([])}
                        className="text-xs text-red-600 hover:text-red-800 mt-2 underline"
                      >
                        Clear errors
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <h4 className="text-sm font-semibold">CSV Format Requirements</h4>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadTemplateCSV();
                    }}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-blue-200 hover:border-blue-300 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    Download Template
                  </button>
                </div>
                <p className="text-xs text-gray-600 mb-2">FlexCOA Assessment format (recommended):</p>
                <code className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded block mb-2 text-[10px]">
                  FORM_NAME, ITEM_ID, ITEM_TYPE, ITEM_TEXT, NUM_CAT, CAT_LABEL_1-5, CAT_CODE_1-5, SCORE_TYPE, SCORE_NAME, SCORE_MODEL, A_1, C_1-4
                </code>
                <p className="text-xs text-gray-500 mb-1">Also supports legacy format: code, text, category, responseType</p>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-2">Alternative Import Methods:</h4>
                <div className="space-y-2">
                  <button 
                    onClick={() => {
                      alert('CDISC ODM Import\n\nThis feature requires backend API implementation:\n\n• POST /api/item-bank/import/cdisc/\n• ODM XML parser\n• Form/ItemGroup selector\n\nComing in Phase 2!');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors"
                  >
                    <Package className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Import from CDISC ODM</div>
                      <div className="text-xs text-gray-500">Import standardized clinical forms</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => {
                      setShowVPGLibrary(true);
                      setShowImportModal(false);
                      fetchQuestionnaires();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors"
                  >
                    <Brain className="w-5 h-5 text-purple-600" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Browse VPG Question Library</div>
                      <div className="text-xs text-gray-500">Pre-validated questionnaires (PHQ-9, GAD-7, PROMIS, etc.)</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
              <button 
                onClick={() => { setShowImportModal(false); setImportErrors([]); setPendingFile(null); }} 
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Summary/Review Modal */}
      {importSummary.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/20 w-full max-w-3xl max-h-[85vh] m-4 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 bg-gradient-to-r from-primary-50 to-blue-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Import Complete</h3>
                <p className="text-sm text-gray-600">
                  {importSummary.items.length} item{importSummary.items.length !== 1 ? 's' : ''} imported successfully
                  {importSummary.errors.length > 0 && (
                    <span className="text-amber-600 ml-2">
                      ({importSummary.errors.length} issue{importSummary.errors.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </p>
              </div>
              <button 
                onClick={() => setImportSummary({ show: false, items: [], errors: [], selectedIds: new Set() })}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Errors Section */}
              {importSummary.errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-amber-800 mb-1">Import Issues</h4>
                      <ul className="text-xs text-amber-700 space-y-1 max-h-24 overflow-y-auto">
                        {importSummary.errors.map((error, idx) => (
                          <li key={idx}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Items List */}
              {importSummary.items.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Imported Items</h4>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setImportSummary(prev => ({
                          ...prev,
                          selectedIds: new Set(prev.items.map(i => i.id))
                        }))}
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        Select All
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => setImportSummary(prev => ({
                          ...prev,
                          selectedIds: new Set()
                        }))}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                    {importSummary.items.map((item) => (
                      <label 
                        key={item.id} 
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={importSummary.selectedIds.has(item.id)}
                          onChange={(e) => {
                            setImportSummary(prev => {
                              const newSelected = new Set(prev.selectedIds);
                              if (e.target.checked) {
                                newSelected.add(item.id);
                              } else {
                                newSelected.delete(item.id);
                              }
                              return { ...prev, selectedIds: newSelected };
                            });
                          }}
                          className="mt-1 w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-600">
                              {item.code}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                              {item.responseType || item.type}
                            </span>
                            {isIrtItem(item) && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                IRT
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mt-1 line-clamp-2">
                            {htmlToPlainText(item.text)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2">
                    {importSummary.selectedIds.size} of {importSummary.items.length} items selected
                    {importSummary.items.filter(i => isIrtItem(i)).length > 0 && (
                      <span className="ml-2 text-purple-600">
                        • {importSummary.items.filter(i => isIrtItem(i)).length} IRT
                      </span>
                    )}
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No items were imported.</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
              <button
                onClick={() => setImportSummary({ show: false, items: [], errors: [], selectedIds: new Set() })}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
              
              <div className="flex items-center gap-3">
                {currentAssessment && importSummary.selectedIds.size > 0 && (
                  <button
                    onClick={() => {
                      const selectedItems = importSummary.items.filter(item =>
                        importSummary.selectedIds.has(item.id)
                      );
                      // Open the Configure Screen Layout modal instead of adding directly
                      setPendingSelectedItems(selectedItems);
                      setShowConfigureLayout(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add {importSummary.selectedIds.size} to Assessment
                  </button>
                )}
                
                <button
                  onClick={() => {
                    // Just close and select items in library view
                    setSelectedItems(Array.from(importSummary.selectedIds));
                    setSelectionMode(true);
                    setViewMode('browse');
                    setImportSummary({ show: false, items: [], errors: [], selectedIds: new Set() });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VPG Question Library Modal */}
      {showVPGLibrary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/20 w-full max-w-5xl h-[80vh] m-4 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">VPG Question Library</h2>
                <p className="text-sm text-gray-600">Browse and import pre-validated clinical questionnaires</p>
              </div>
              <button onClick={() => { setShowVPGLibrary(false); setVpgError(''); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b">
              <button
                onClick={() => setVpgTab('questionnaires')}
                className={`px-6 py-3 font-medium transition-colors ${
                  vpgTab === 'questionnaires'
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Standard Questionnaires
              </button>
              <button
                onClick={() => setVpgTab('search')}
                className={`px-6 py-3 font-medium transition-colors ${
                  vpgTab === 'search'
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Search All Questions
              </button>
            </div>

            {/* VPG Error Display */}
            {vpgError && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700 flex-1">{vpgError}</span>
                  <button
                    onClick={() => setVpgError('')}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {vpgTab === 'questionnaires' ? (
                <div className="space-y-4">
                  {vpgLoading && !selectedQuestionnaire ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                    </div>
                  ) : !selectedQuestionnaire ? (
                    questionnaires.map((q) => (
                      <div key={q.id} className="border rounded-lg p-4 hover:border-primary-300 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900">{q.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">{q.description}</p>
                            <div className="flex gap-4 mt-2 text-xs text-gray-500">
                              <span>📋 {q.question_count} questions</span>
                              <span>🏷️ {q.category}</span>
                              <span>🎯 {q.domain}</span>
                              {q.validated && <span className="text-green-600">✓ Validated</span>}
                            </div>
                            {q.copyright && (
                              <p className="text-xs text-gray-400 mt-2">{q.copyright}</p>
                            )}
                          </div>
                          <button
                            onClick={() => fetchQuestionnaireQuestions(q.id)}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 whitespace-nowrap ml-4"
                          >
                            View Questions
                          </button>
                        </div>
                      </div>
                    ))
                  ) : null}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Search Filters */}
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Search by question text, code, or tags..."
                        value={vpgSearchQuery}
                        onChange={(e) => setVpgSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Domain..."
                      value={vpgDomainFilter}
                      onChange={(e) => setVpgDomainFilter(e.target.value)}
                      className="w-48 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      onClick={() => searchVPGLibrary(vpgSearchQuery, vpgDomainFilter)}
                      className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Search
                    </button>
                  </div>

                  {/* Search Results */}
                  {vpgLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {vpgQuestions.map((q) => (
                        <div key={q.id} className="border rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedVPGQuestions.includes(q.id)}
                              onChange={() => {
                                setSelectedVPGQuestions(prev =>
                                  prev.includes(q.id)
                                    ? prev.filter(id => id !== q.id)
                                    : [...prev, q.id]
                                );
                              }}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <span className="text-xs font-mono text-gray-500">{q.code}</span>
                                  <p className="text-sm font-medium text-gray-900">{q.text}</p>
                                  <div className="flex gap-2 mt-1">
                                    {q.domain && (
                                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                        {q.domain}
                                      </span>
                                    )}
                                    {q.validated && (
                                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                        Validated
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Selected Questionnaire View */}
              {selectedQuestionnaire && (
                <div className="mt-6 border-t pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedQuestionnaire.name}</h3>
                      <p className="text-sm text-gray-600">{selectedQuestionnaire.description}</p>
                      {selectedQuestionnaire.scoring && (
                        <p className="text-xs text-gray-500 mt-2">
                          <strong>Scoring:</strong> {selectedQuestionnaire.scoring}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedQuestionnaire(null);
                        setVpgQuestions([]);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        {vpgLoading ? 'Loading...' : `${vpgQuestions.length} Questions`}
                      </span>
                      <button
                        onClick={() => {
                          const allIds = vpgQuestions.map(q => q.id);
                          setSelectedVPGQuestions(
                            selectedVPGQuestions.length === vpgQuestions.length ? [] : allIds
                          );
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700"
                        disabled={vpgLoading}
                      >
                        {selectedVPGQuestions.length === vpgQuestions.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {vpgLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                      </div>
                    ) : vpgQuestions.map((q, idx) => (
                      <div key={q.id} className="border rounded p-3 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedVPGQuestions.includes(q.id)}
                            onChange={() => {
                              setSelectedVPGQuestions(prev =>
                                prev.includes(q.id)
                                  ? prev.filter(id => id !== q.id)
                                  : [...prev, q.id]
                              );
                            }}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="text-xs font-mono text-gray-500">
                                  Q{idx + 1}: {q.code}
                                </span>
                                <p className="text-sm text-gray-900 mt-1">{q.text}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t dark:border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {selectedVPGQuestions.length > 0 && (
                    <span>{selectedVPGQuestions.length} question(s) selected</span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowVPGLibrary(false); setVpgError(''); }}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={importVPGQuestions}
                    disabled={selectedVPGQuestions.length === 0 || vpgLoading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {vpgLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Import to Sponsor Library
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Preview Modal */}
      {previewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 w-full max-w-2xl max-h-[85vh] overflow-hidden m-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Item Preview</h3>
                <p className="text-primary-100 text-sm mt-0.5">{previewItem.code}</p>
              </div>
              <button 
                onClick={() => setPreviewItem(null)} 
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              {/* Question Text */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Question Text</h4>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-gray-800" dangerouslySetInnerHTML={{ __html: previewItem.text || '' }} />
                </div>
              </div>

              {/* Response Configuration - Type-specific display */}
              {(() => {
                const options = previewItem.responseOptions || previewItem.options;
                const responseType = previewItem.responseType || previewItem.type || 'ordinal';
                
                // VAS Configuration
                if (responseType === 'vas' && options && typeof options === 'object' && !Array.isArray(options)) {
                  return (
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Visual Analog Scale Configuration
                      </h4>
                      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-center">
                            <div className="text-xs text-blue-600 font-medium mb-1">Left Anchor (0)</div>
                            <div className="text-sm text-blue-800 font-semibold">{options.leftAnchor || 'Not set'}</div>
                          </div>
                          <div className="flex-1 mx-4 h-2 bg-gradient-to-r from-blue-200 to-blue-500 rounded-full" />
                          <div className="text-center">
                            <div className="text-xs text-blue-600 font-medium mb-1">Right Anchor (100)</div>
                            <div className="text-sm text-blue-800 font-semibold">{options.rightAnchor || 'Not set'}</div>
                          </div>
                        </div>
                        <div className="text-xs text-blue-600 text-center">Continuous slider from 0 to 100</div>
                      </div>
                    </div>
                  );
                }
                
                // Numeric Configuration
                if (responseType === 'numeric' && options && typeof options === 'object' && !Array.isArray(options)) {
                  return (
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Numeric Rating Scale Configuration
                      </h4>
                      <div className="bg-indigo-50 rounded-lg border border-indigo-200 p-4">
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div className="text-center">
                            <div className="text-xs text-indigo-600 font-medium mb-1">Min Value</div>
                            <div className="text-lg text-indigo-800 font-bold">{options.minValue ?? 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-indigo-600 font-medium mb-1">Max Value</div>
                            <div className="text-lg text-indigo-800 font-bold">{options.maxValue ?? 10}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-indigo-600 font-medium mb-1">Step</div>
                            <div className="text-lg text-indigo-800 font-bold">{options.step ?? 1}</div>
                          </div>
                        </div>
                        {(options.leftAnchor || options.rightAnchor) && (
                          <div className="flex justify-between text-sm text-indigo-700 border-t border-indigo-200 pt-3 mt-2">
                            <span>{options.leftAnchor || ''}</span>
                            <span>{options.rightAnchor || ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                
                // Text Configuration
                if (responseType === 'text' && options && typeof options === 'object' && !Array.isArray(options)) {
                  return (
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Free Text Configuration
                      </h4>
                      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-300 p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-gray-600 font-medium mb-1">Max Length</div>
                            <div className="text-sm text-gray-800 font-semibold">{options.maxLength ?? 500} characters</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600 font-medium mb-1">Input Type</div>
                            <div className="text-sm text-gray-800 font-semibold">{options.multiline ? 'Multi-line (textarea)' : 'Single-line'}</div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs text-gray-600 font-medium mb-1">Validation Rule</div>
                            <div className="text-sm text-gray-800 font-semibold">
                              {describeFreeTextValidation(getFreeTextValidation(options)) || 'None (any text accepted)'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Boolean Configuration
                if (responseType === 'boolean' && options && typeof options === 'object' && !Array.isArray(options)) {
                  return (
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Yes/No Configuration
                      </h4>
                      <div className="bg-green-50 rounded-lg border border-green-200 p-4">
                        <div className="flex items-center justify-center gap-8">
                          <div className="text-center">
                            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-2">
                              <Check className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-sm text-green-800 font-semibold">{options.yesLabel || 'Yes'}</div>
                          </div>
                          <div className="text-gray-400 text-xl">/</div>
                          <div className="text-center">
                            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mb-2">
                              <X className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-sm text-red-800 font-semibold">{options.noLabel || 'No'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Ordinal/Nominal - Array of options
                if (Array.isArray(options) && options.length > 0) {
                  return (
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Response Options ({options.length})
                      </h4>
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Label</th>
                              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-20">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {options.map((opt, idx) => (
                              <tr key={idx} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                                <td className="px-4 py-2.5">
                                  <span className="w-6 h-6 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                                    {idx + 1}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-gray-700" dangerouslySetInnerHTML={{ __html: opt.label || opt.text || opt }} />
                                <td className="px-4 py-2.5 text-center">
                                  <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs font-mono">
                                    {opt.value !== undefined ? opt.value : idx}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }
                
                return null;
              })()}

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-4">
                {previewItem.category && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <h5 className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide mb-1">Category</h5>
                    <p className="text-sm text-blue-800 font-medium">{previewItem.category}</p>
                  </div>
                )}
                {previewItem.domain && (
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                    <h5 className="text-[10px] font-semibold text-purple-600 uppercase tracking-wide mb-1">Domain</h5>
                    <p className="text-sm text-purple-800 font-medium">{previewItem.domain}</p>
                  </div>
                )}
                {previewItem.responseType && (
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                    <h5 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-1">Response Type</h5>
                    <p className="text-sm text-amber-800 font-medium capitalize">{previewItem.responseType}</p>
                  </div>
                )}
                {previewItem.source && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <h5 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Source</h5>
                    <p className="text-sm text-gray-700 font-medium">{previewItem.source}</p>
                  </div>
                )}
                {previewItem.validated && (
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <h5 className="text-[10px] font-semibold text-green-600 uppercase tracking-wide mb-1">Validation Status</h5>
                    <p className="text-sm text-green-800 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Validated
                    </p>
                  </div>
                )}
                {(previewItem.tags?.length > 0) && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700 col-span-2">
                    <h5 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Tags</h5>
                    <div className="flex flex-wrap gap-1.5">
                      {(Array.isArray(previewItem.tags) ? previewItem.tags : []).map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* IRT Parameters (if available) */}
              {(previewItem.irtParameters && Object.keys(previewItem.irtParameters).length > 0) && (
                <div className="mt-6 bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                  <h4 className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> IRT Parameters
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {previewItem.slope !== null && previewItem.slope !== undefined && (
                      <div className="bg-white dark:bg-gray-800 rounded p-2 border border-indigo-200">
                        <span className="text-[10px] text-indigo-500 uppercase">Slope (a)</span>
                        <p className="text-sm font-mono font-medium text-indigo-800">{previewItem.slope}</p>
                      </div>
                    )}
                    {previewItem.thresholds && (
                      <div className="bg-white dark:bg-gray-800 rounded p-2 border border-indigo-200 col-span-2">
                        <span className="text-[10px] text-indigo-500 uppercase">Thresholds (b)</span>
                        <p className="text-sm font-mono font-medium text-indigo-800">
                          {Array.isArray(previewItem.thresholds) ? previewItem.thresholds.join(', ') : previewItem.thresholds}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <span className="text-xs text-gray-500">ID: {previewItem.id}</span>
              <div className="flex gap-2">
                {currentAssessment && (
                  <button
                    onClick={async () => {
                      const cloned = await cloneItemsForAssessment([previewItem]);
                      if (!cloned || cloned.length === 0) return;
                      const cloneItem = cloned[0];
                      const irtParams = cloneItem.irtParameters || {};
                      const mappedIrtParams = {
                        ...irtParams,
                        slope: irtParams.discrimination || cloneItem.slope || null,
                        thresholds: irtParams.thresholds || cloneItem.thresholds || null
                      };
                      onAddItem({
                        type: 'question',
                        itemData: { ...cloneItem, irt_parameters: mappedIrtParams },
                        responseOptions: cloneItem.responseOptions
                      });
                      setSaveStatus({ type: 'success', message: `Added "${cloneItem.code}"` });
                      setPreviewItem(null);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add to Assessment
                  </button>
                )}
                <button
                  onClick={() => setPreviewItem(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type || (confirmModal.title.includes('Delete') ? 'danger' : 'warning')}
        confirmText={confirmModal.confirmText || 'OK'}
        size={confirmModal.size || 'md'}
      />

      {/* Configure Screen Layout Modal — shown after import Add to Assessment */}
      <ConfigureScreenLayoutModal
        show={showConfigureLayout}
        onClose={() => setShowConfigureLayout(false)}
        assessmentId={currentAssessment?.assessment_uuid}
        selectedItems={pendingSelectedItems}
        deviceMode={deviceMode}
        onSkip={(selectedItems) => {
          // Add items to sidebar without auto-building screens
          const itemsToAdd = selectedItems.map(item => ({
            type: 'question',
            itemData: {
              ...item,
              item_bank_id: item.id,
              irt_parameters: {
                ...(item.irtParameters || {}),
                slope: item.irtParameters?.discrimination || item.slope || null,
                thresholds: item.irtParameters?.thresholds || item.thresholds || null
              }
            },
            responseOptions: item.responseOptions || item.response_options || item.options || []
          }));
          onAddItem(itemsToAdd);
          setSaveStatus({
            type: 'success',
            message: `Added ${selectedItems.length} items to sidebar`
          });
          setImportSummary({ show: false, items: [], errors: [], selectedIds: new Set() });
          setShowConfigureLayout(false);
          setPendingSelectedItems([]);
          onClose();
        }}
        onBuildComplete={(selectedItems, result) => {
          // Build itemsToAdd array with IRT mapping (same as original handler)
          const itemsToAdd = selectedItems.map(item => {
            const irtParams = item.irtParameters || {};
            const mappedIrtParams = {
              ...irtParams,
              slope: irtParams.discrimination || item.slope || null,
              thresholds: irtParams.thresholds || item.thresholds || null
            };
            return {
              type: 'question',
              itemData: {
                ...item,
                item_bank_id: item.id,
                irt_parameters: mappedIrtParams
              },
              responseOptions: item.responseOptions || []
            };
          });
          // Sync sidebar
          onAddItem(itemsToAdd);
          // Trigger ScreenBuilder re-fetch to pick up auto-built screens
          if (onScreensBuilt) onScreensBuilt();
          setSaveStatus({
            type: 'success',
            message: `Built ${result.screens_created} screens with ${selectedItems.length} items`
          });
          setImportSummary({ show: false, items: [], errors: [], selectedIds: new Set() });
          setShowConfigureLayout(false);
          setPendingSelectedItems([]);
          onClose();
        }}
      />
    </div>
  );
};

export default UnifiedItemBank;
